import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { financialApi, subscriptionsApi } from "../services/api";
import ModuleHeader from "../components/shared/ModuleHeader";
import SavingNotice from "../components/shared/SavingNotice";
import CenteredModal from "../components/shared/CenteredModal";
import ConfirmModal from "../components/shared/ConfirmModal";
import { SheetLogoRow } from "../components/shared/SheetChrome";
import useMinLoad from "../hooks/useMinLoad";
import useFormPresentation from "../hooks/useFormPresentation";
import useBackButton from "../hooks/useBackButton";
import { useAuth } from "../hooks/useAuth";

const IMG_SUBS = "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1400&q=75&auto=format&fit=crop";

// ── Quick-add library ─────────────────────────────────────────────────────────

const QUICK_ADD = {
  streaming: [
    { name: "Netflix",      emoji: "🎬", amount: 15.49, url: "netflix.com"       },
    { name: "Hulu",         emoji: "📺", amount: 7.99,  url: "hulu.com"          },
    { name: "Disney+",      emoji: "🏰", amount: 7.99,  url: "disneyplus.com"    },
    { name: "HBO Max",      emoji: "🎭", amount: 9.99,  url: "max.com"           },
    { name: "Apple TV+",    emoji: "🍎", amount: 9.99,  url: "tv.apple.com"      },
    { name: "Peacock",      emoji: "🦚", amount: 5.99,  url: "peacocktv.com"     },
    { name: "Paramount+",   emoji: "⭐", amount: 5.99,  url: "paramountplus.com" },
    { name: "ESPN+",        emoji: "🏈", amount: 10.99, url: "espnplus.com"      },
    { name: "YouTube Premium", emoji: "▶️", amount: 13.99, url: "youtube.com"   },
    { name: "Crunchyroll",  emoji: "🍜", amount: 7.99,  url: "crunchyroll.com"  },
    { name: "AMC+",         emoji: "🎞️", amount: 8.99,  url: "amcplus.com"      },
    { name: "Showtime",     emoji: "🎬", amount: 10.99, url: "showtime.com"     },
  ],
  music: [
    { name: "Spotify",       emoji: "🎵", amount: 10.99, url: "spotify.com"       },
    { name: "Apple Music",   emoji: "🎵", amount: 10.99, url: "music.apple.com"   },
    { name: "Amazon Music",  emoji: "🎵", amount: 8.99,  url: "music.amazon.com"  },
    { name: "Tidal",         emoji: "🌊", amount: 10.99, url: "tidal.com"         },
    { name: "Pandora",       emoji: "🎸", amount: 4.99,  url: "pandora.com"       },
    { name: "Audible",       emoji: "📚", amount: 14.95, url: "audible.com"       },
    { name: "SiriusXM",      emoji: "📡", amount: 9.99,  url: "siriusxm.com"      },
    { name: "iHeartRadio",   emoji: "📻", amount: 4.99,  url: "iheart.com"        },
  ],
  productivity: [
    { name: "Microsoft 365",        emoji: "💼", amount: 9.99,  url: "microsoft.com"   },
    { name: "Google One",            emoji: "☁️",  amount: 2.99,  url: "one.google.com"  },
    { name: "Adobe Creative Cloud", emoji: "🎨", amount: 54.99, url: "adobe.com"       },
    { name: "Dropbox",               emoji: "📦", amount: 11.99, url: "dropbox.com"     },
    { name: "iCloud+",               emoji: "☁️",  amount: 2.99,  url: "icloud.com"      },
    { name: "Notion",                emoji: "📝", amount: 8.00,  url: "notion.so"       },
    { name: "Slack",                 emoji: "💬", amount: 7.25,  url: "slack.com"       },
    { name: "Zoom",                  emoji: "📹", amount: 15.99, url: "zoom.us"         },
    { name: "1Password",             emoji: "🔐", amount: 2.99,  url: "1password.com"   },
    { name: "LastPass",              emoji: "🔑", amount: 3.00,  url: "lastpass.com"    },
    { name: "Grammarly",             emoji: "✍️",  amount: 12.00, url: "grammarly.com"   },
  ],
  food: [
    { name: "Amazon Prime",    emoji: "📦", amount: 14.99, url: "amazon.com"       },
    { name: "DoorDash DashPass", emoji: "🚗", amount: 9.99, url: "doordash.com"   },
    { name: "Uber One",         emoji: "🚙", amount: 9.99,  url: "uber.com"        },
    { name: "Instacart+",       emoji: "🛒", amount: 9.99,  url: "instacart.com"   },
    { name: "HelloFresh",       emoji: "🥗", amount: 79.99, url: "hellofresh.com"  },
    { name: "EveryPlate",       emoji: "🍽️", amount: 39.99, url: "everyplate.com"  },
    { name: "Factor",           emoji: "🥘", amount: 60.00, url: "factor75.com"    },
    { name: "Walmart+",         emoji: "🛍️", amount: 12.95, url: "walmart.com"     },
  ],
  fitness: [
    { name: "Peloton",           emoji: "🚴", amount: 44.00, url: "onepeloton.com"  },
    { name: "Calm",              emoji: "🧘", amount: 14.99, url: "calm.com"        },
    { name: "Headspace",         emoji: "🧠", amount: 12.99, url: "headspace.com"   },
    { name: "MyFitnessPal",      emoji: "💪", amount: 19.99, url: "myfitnesspal.com"},
    { name: "Noom",              emoji: "🥦", amount: 59.00, url: "noom.com"        },
    { name: "ClassPass",         emoji: "🏋️", amount: 29.00, url: "classpass.com"   },
    { name: "Apple Fitness+",    emoji: "⌚", amount: 9.99,  url: "apple.com"       },
  ],
  finance: [
    { name: "AAA",            emoji: "🚘", amount: 12.00, url: "aaa.com"          },
    { name: "LifeLock",       emoji: "🛡️", amount: 9.99,  url: "lifelock.com"     },
    { name: "Experian",       emoji: "📊", amount: 24.99, url: "experian.com"     },
    { name: "YNAB",           emoji: "💰", amount: 14.99, url: "youneedabudget.com"},
    { name: "Rocket Money",   emoji: "🚀", amount: 4.00,  url: "rocketmoney.com"  },
    { name: "QuickBooks",     emoji: "📒", amount: 30.00, url: "quickbooks.com"   },
    { name: "TurboTax",       emoji: "🧾", amount: 0,     url: "turbotax.com"     },
    { name: "NerdWallet",     emoji: "🤓", amount: 0,     url: "nerdwallet.com"   },
  ],
  gaming: [
    { name: "Xbox Game Pass",      emoji: "🎮", amount: 14.99, url: "xbox.com"         },
    { name: "PlayStation Plus",    emoji: "🎮", amount: 13.99, url: "playstation.com"  },
    { name: "Nintendo Online",     emoji: "🎮", amount: 3.99,  url: "nintendo.com"     },
    { name: "EA Play",             emoji: "⚽", amount: 4.99,  url: "ea.com"           },
    { name: "Apple Arcade",        emoji: "🕹️", amount: 6.99,  url: "apple.com"        },
    { name: "Google Play Pass",    emoji: "🎯", amount: 4.99,  url: "play.google.com"  },
  ],
  news: [
    { name: "New York Times",    emoji: "📰", amount: 17.00, url: "nytimes.com"     },
    { name: "Wall Street Journal", emoji: "📈", amount: 38.99, url: "wsj.com"       },
    { name: "Washington Post",   emoji: "📰", amount: 10.00, url: "washingtonpost.com"},
    { name: "The Athletic",      emoji: "🏆", amount: 7.99,  url: "theathletic.com" },
    { name: "Medium",            emoji: "✍️",  amount: 5.00,  url: "medium.com"      },
  ],
};

const CATEGORY_META = {
  streaming:   { label: "Streaming",       emoji: "🎬" },
  music:       { label: "Music",           emoji: "🎵" },
  productivity:{ label: "Productivity",    emoji: "💼" },
  food:        { label: "Food & Delivery", emoji: "🍽️" },
  fitness:     { label: "Fitness & Health",emoji: "💪" },
  finance:     { label: "Finance",         emoji: "💰" },
  gaming:      { label: "Gaming",          emoji: "🎮" },
  news:        { label: "News & Media",    emoji: "📰" },
  software:    { label: "Software",        emoji: "💻" },
  other:       { label: "Other",           emoji: "📋" },
};

const QUICK_ADD_CATEGORY_LABELS = {
  streaming: "Streaming",
  music: "Music",
  productivity: "Productivity",
  food: "Food",
  fitness: "Fitness",
  finance: "Finance",
  gaming: "Gaming",
  news: "News",
};

const BILLING_CYCLES = [
  { value: "monthly",   label: "Monthly"   },
  { value: "annual",    label: "Annual"    },
  { value: "quarterly", label: "Quarterly" },
  { value: "weekly",    label: "Weekly"    },
];

const STATUS_CONFIG = {
  active:    { cls: "hm-sub-status-active",    label: "Active"    },
  paused:    { cls: "hm-sub-status-paused",    label: "Paused"    },
  cancelled: { cls: "hm-sub-status-cancelled", label: "Cancelled" },
  trial:     { cls: "hm-sub-status-trial",     label: "Trial"     },
};

const FAMILY_PLAN_HINTS = {
  "spotify": { familyPrice: 16.99, label: "Spotify Family" },
  "apple music": { familyPrice: 16.99, label: "Apple Music Family" },
  "youtube premium": { familyPrice: 22.99, label: "YouTube Premium Family" },
  "hulu": { familyPrice: 17.99, label: "Hulu bundle" },
  "disney+": { familyPrice: 19.99, label: "Disney bundle" },
};

const BLANK_FORM = {
  name: "", provider: "", category: "streaming", amount: "",
  billing_cycle: "monthly", next_renewal_date: "", trial_end_date: "",
  start_date: "", status: "active", is_shared: false, shared_member_count: 2,
  payment_method: "", notes: "", website_url: "", logo_emoji: "",
  usage_rating: "",
};

// Flat catalog for typeahead — built from QUICK_ADD at module load time
const PROVIDER_CATALOG = Object.entries(QUICK_ADD).flatMap(([cat, items]) =>
  items.map(item => ({ ...item, category: cat }))
);

function ProviderNameInput({ value, onChange, onSelect }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen]               = useState(false);
  const [focused, setFocused]         = useState(-1);
  const containerRef = useRef(null);

  const handleChange = (e) => {
    const q = e.target.value;
    onChange(q);
    if (q.trim().length < 1) { setSuggestions([]); setOpen(false); return; }
    const lower = q.toLowerCase();
    const matches = PROVIDER_CATALOG.filter(p => p.name.toLowerCase().includes(lower)).slice(0, 6);
    setSuggestions(matches);
    setOpen(matches.length > 0);
    setFocused(-1);
  };

  const pick = (provider) => {
    onSelect(provider);
    setSuggestions([]);
    setOpen(false);
    setFocused(-1);
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setFocused(f => Math.min(f + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setFocused(f => Math.max(f - 1, -1)); }
    else if (e.key === "Enter" && focused >= 0) { e.preventDefault(); pick(suggestions[focused]); }
    else if (e.key === "Escape") { setOpen(false); setFocused(-1); }
  };

  useEffect(() => {
    const handleClick = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
      <input
        className="hm-auth-input"
        placeholder="Netflix, Spotify..."
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
        autoComplete="off"
      />
      {open && (
        <ul style={{
          position: "absolute", zIndex: 200, top: "calc(100% + 4px)", left: 0, right: 0,
          margin: 0, padding: "4px 0", listStyle: "none",
          background: "var(--color-surface, #fff)", border: "1px solid var(--color-border, #e5e7eb)",
          borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", maxHeight: 240, overflowY: "auto",
        }}>
          {suggestions.map((p, i) => (
            <li key={p.name} onMouseDown={() => pick(p)} style={{
              display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
              cursor: "pointer", fontSize: 14,
              background: i === focused ? "var(--color-primary-soft, #f0f4ff)" : "transparent",
            }}>
              <span style={{ fontSize: 18, width: 24, textAlign: "center" }}>{p.emoji}</span>
              <span style={{ fontWeight: 500 }}>{p.name}</span>
              <span style={{ marginLeft: "auto", color: "var(--color-text-muted, #6b7280)", fontSize: 12 }}>
                ${p.amount.toFixed(2)}/mo
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt$(n) {
  if (n == null || n === "") return "—";
  return `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function daysUntil(iso) {
  if (!iso) return null;
  const diff = Math.ceil((new Date(iso) - new Date()) / 86400000);
  return diff;
}

function dateFromIso(iso) {
  if (!iso) return null;
  const [year, month, day] = String(iso).split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function isoFromDate(date) {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function addMonthsClamped(date, months) {
  const next = new Date(date);
  const targetMonth = next.getMonth() + months;
  const targetYear = next.getFullYear() + Math.floor(targetMonth / 12);
  const normalizedMonth = ((targetMonth % 12) + 12) % 12;
  const lastDay = new Date(targetYear, normalizedMonth + 1, 0).getDate();
  next.setFullYear(targetYear, normalizedMonth, Math.min(next.getDate(), lastDay));
  return next;
}

function advanceRenewalDate(iso, cycle = "monthly", forceAdvance = false) {
  let next = dateFromIso(iso);
  if (!next) return "";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const normalizedCycle = String(cycle || "monthly").toLowerCase();
  const step = () => {
    if (normalizedCycle === "weekly") next.setDate(next.getDate() + 7);
    else if (normalizedCycle === "annual" || normalizedCycle === "yearly") next = addMonthsClamped(next, 12);
    else if (normalizedCycle === "quarterly") next = addMonthsClamped(next, 3);
    else next = addMonthsClamped(next, 1);
  };
  if (forceAdvance) step();
  let guard = 0;
  while (next <= today && guard < 48) {
    step();
    guard += 1;
  }
  return isoFromDate(next);
}

function sharedSplitText(sub) {
  if (!sub?.is_shared) return "";
  const count = Number(sub.shared_member_count || 0);
  if (count > 1 && sub.shared_cost_per_person != null) {
    return `${fmt$(sub.shared_cost_per_person)} each · ${count} people`;
  }
  return "Shared";
}

function signalSourceLabel(finding) {
  const type = finding?.type || "";
  if (type.includes("meal") || type.includes("delivery")) return "Food";
  if (type.includes("roadside")) return "Vehicles";
  if (type.includes("warranty")) return "Home Vault";
  if (type.includes("security") || type.includes("cable") || type.includes("streaming")) return "Utilities";
  if (type.includes("fitness")) return "Health";
  if (type.includes("seasonal")) return "Gifts";
  return "Household";
}

function signalReason(finding) {
  if (!finding) return "";
  return finding.message || finding.title || "";
}

function scriptReasonFromFinding(finding, subName = "this subscription") {
  const reason = signalReason(finding);
  if (!reason) return "";
  return `${subName}: ${reason}`;
}

function renewalLabel(days) {
  if (days == null) return null;
  if (days < 0)  return { text: "Update date", cls: "hm-sub-renew-stale" };
  if (days === 0) return { text: "Today",  cls: "hm-sub-renew-urgent"  };
  if (days <= 3)  return { text: `${days}d`, cls: "hm-sub-renew-urgent"  };
  if (days <= 7)  return { text: `${days}d`, cls: "hm-sub-renew-soon"    };
  return { text: `${days}d`, cls: "hm-sub-renew-ok" };
}

function subscriptionEdgeClass(sub, renewalDays, lowUse = false) {
  if (sub.status === "cancelled" || sub.status === "paused") return "is-past";
  const trialDays = sub.status === "trial" ? daysUntil(sub.trial_end_date) : null;
  const focusDays = trialDays ?? renewalDays;
  if (focusDays != null && focusDays <= 3) return "is-urgent";
  if (focusDays != null && focusDays <= 14) return "is-soon";
  if (lowUse) return "is-soon";
  return "is-calm";
}

function MobileCollapseSection({ storageKey, title, badge, defaultOpen = false, children }) {
  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved == null ? defaultOpen : saved === "true";
    } catch {
      return defaultOpen;
    }
  });

  const toggle = () => {
    setOpen(current => {
      const next = !current;
      try { localStorage.setItem(storageKey, String(next)); } catch {}
      return next;
    });
  };

  return (
    <section className={`hm-mobile-collapse${open ? " is-open" : ""}`}>
      <button type="button" className="hm-mobile-collapse-toggle" onClick={toggle} aria-expanded={open}>
        <span>{title}</span>
        {badge && <em>{badge}</em>}
        <b>{open ? "⌃" : "⌄"}</b>
      </button>
      <div className="hm-mobile-collapse-body">{children}</div>
    </section>
  );
}

function fmtShort$(n) {
  if (!n) return "$0";
  return `$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

function getUsageRating(sub) {
  const rating = Number(sub.usage_rating || 0);
  return rating > 0 ? rating : null;
}

function isLowUseSubscription(sub) {
  const rating = getUsageRating(sub);
  if (rating != null) return rating <= 2;
  const loggedUses = Number(sub.usage_count ?? sub.usage_count_30d ?? 0);
  return loggedUses > 0 && loggedUses <= 1;
}

function reviewableSavingsFactor(sub) {
  const rating = getUsageRating(sub);
  if (rating === 1) return 1;
  if (rating === 2) return 0.5;
  return 0.5;
}

function usageDetail(sub) {
  const rating = getUsageRating(sub);
  if (rating === 1) return "Marked rarely used.";
  if (rating === 2) return "Marked occasionally used.";
  if (rating >= 3) return "Marked regularly used.";
  const count = Number(sub.usage_count ?? sub.usage_count_30d ?? 0);
  const label = sub.usage_window_label || "last 30 days";
  return `${count} use${count === 1 ? "" : "s"} · ${label}.`;
}

function usageWindowFor(sub, usageLogs = []) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 29);
  const created = sub?.created_at ? new Date(sub.created_at) : null;
  if (created) created.setHours(0, 0, 0, 0);
  let start = created && created > thirtyDaysAgo ? created : thirtyDaysAgo;
  usageLogs.forEach(log => {
    if (!log?.log_date) return;
    const logDate = new Date(`${log.log_date}T00:00:00`);
    if (logDate >= thirtyDaysAgo && logDate < start) start = logDate;
  });
  const days = Math.max(1, Math.min(30, Math.round((today - start) / 86400000) + 1));
  const count = usageLogs.filter(log => {
    if (!log?.used_today || !log.log_date) return false;
    const logDate = new Date(`${log.log_date}T00:00:00`);
    return logDate >= start;
  }).length;
  const label = days >= 30 ? "30-day use" : (created && start.getTime() === created.getTime() ? "Since added" : `${days}-day use`);
  const helper = days >= 30 ? "Last 30 days" : `${count}x · ${days} day${days === 1 ? "" : "s"} tracked`;
  return { days, count, label, helper };
}

function normalizeUrl(url) {
  const clean = String(url || "").trim();
  if (!clean) return "";
  return /^https?:\/\//i.test(clean) ? clean : `https://${clean}`;
}

function scriptCancelUrl(sub, result) {
  return normalizeUrl(
    result?.direct_cancel_url ||
    result?.cancel_url ||
    result?.direct_cancel_link ||
    sub?.cancel_url ||
    sub?.website_url
  );
}

function copyToClipboard(text) {
  const value = String(text || "").trim();
  if (!value || typeof navigator === "undefined" || !navigator.clipboard) return;
  navigator.clipboard.writeText(value).catch(() => {});
}

function useBodyScrollLock(locked) {
  useEffect(() => {
    if (!locked || typeof document === "undefined") return undefined;
    const body = document.body;
    const html = document.documentElement;
    const previousBodyOverflow = body.style.overflow;
    const previousHtmlOverflow = html.style.overflow;
    body.style.overflow = "hidden";
    html.style.overflow = "hidden";
    return () => {
      body.style.overflow = previousBodyOverflow;
      html.style.overflow = previousHtmlOverflow;
    };
  }, [locked]);
}

function summarizeSubscriptionHealth(subs, totalMonthly) {
  const active = subs.filter(s => s.status === "active" || s.status === "trial");
  const annualExposure = totalMonthly * 12;
  const trialsEnding = subs.filter(s => {
    const d = daysUntil(s.trial_end_date);
    return d != null && d >= 0 && d <= 14;
  });
  const renewalsSoon = active.filter(s => {
    const d = daysUntil(s.next_renewal_date);
    return d != null && d >= 0 && d <= 7;
  });
  const missingDates = active.filter(s => !s.next_renewal_date);
  const lowUse = active.filter(s => isLowUseSubscription(s) && (s.monthly_amount || 0) >= 10);
  const annualSoon = active.filter(s => {
    const d = daysUntil(s.next_renewal_date);
    return s.billing_cycle === "annual" && d != null && d >= 0 && d <= 30;
  });
  const categoryCounts = active.reduce((acc, s) => {
    acc[s.category || "other"] = (acc[s.category || "other"] || 0) + 1;
    return acc;
  }, {});
  const crowdedCategories = Object.entries(categoryCounts)
    .filter(([, count]) => count >= 3)
    .map(([category, count]) => ({ category, count }));
  const byName = active.reduce((acc, s) => {
    const key = (s.name || "").toLowerCase().replace(/\s+/g, " ").trim();
    if (!key) return acc;
    acc[key] = acc[key] || [];
    acc[key].push(s);
    return acc;
  }, {});
  const duplicates = Object.entries(byName)
    .filter(([, items]) => items.length > 1)
    .map(([name, items]) => {
      const total = items.reduce((sum, s) => sum + (s.monthly_amount || 0), 0);
      const hint = FAMILY_PLAN_HINTS[name];
      const monthlySavings = hint ? Math.max(0, total - hint.familyPrice) : 0;
      return { name, items, total, hint, monthlySavings };
    });
  const estimatedYearlySavings =
    lowUse.reduce((sum, s) => sum + ((s.monthly_amount || 0) * 12 * reviewableSavingsFactor(s)), 0) +
    duplicates.reduce((sum, d) => sum + (d.monthlySavings * 12), 0);
  const scorePenalty =
    trialsEnding.length * 10 +
    renewalsSoon.length * 6 +
    annualSoon.length * 8 +
    lowUse.length * 9 +
    crowdedCategories.length * 7 +
    duplicates.length * 12 +
    Math.min(missingDates.length * 3, 12);
  const healthScore = Math.max(35, Math.min(100, 100 - scorePenalty));

  let healthLabel = "Calm";
  if (trialsEnding.length || renewalsSoon.length || annualSoon.length) healthLabel = "Review soon";
  if (lowUse.length || crowdedCategories.length || duplicates.length) healthLabel = "Savings likely";

  return {
    active, annualExposure, trialsEnding, renewalsSoon, missingDates, lowUse,
    annualSoon, crowdedCategories, duplicates, estimatedYearlySavings,
    healthScore, healthLabel,
  };
}

function SubscriptionActionCard({ item, onOpen }) {
  return (
    <button type="button" className={`hm-sub-action-card hm-sub-action-${item.tone || "neutral"}`} onClick={onOpen}>
      <span>{item.kicker}</span>
      <strong>{item.title}</strong>
      <small>{item.detail}</small>
    </button>
  );
}

function clampPercent(value) {
  return Math.max(8, Math.min(100, Math.round(value)));
}

function SubscriptionHealthPanel({ health }) {
  const ring = 138;
  const offset = ring - (ring * health.healthScore / 100);
  const bars = [
    ["Usage match", clampPercent(100 - health.lowUse.length * 22), "teal"],
    ["No duplicates", clampPercent(100 - health.duplicates.length * 25), health.duplicates.length ? "amber" : "teal"],
    ["Cost vs value", clampPercent(100 - health.estimatedYearlySavings / 18), health.estimatedYearlySavings ? "amber" : "teal"],
    ["Renewal ready", clampPercent(100 - (health.renewalsSoon.length + health.missingDates.length) * 15), health.renewalsSoon.length ? "red" : "teal"],
  ];

  return (
    <section className="hm-sub-health-panel" aria-label="Subscription health">
      <div className="hm-sub-section-kicker">Subscription health</div>
      <div className="hm-sub-health-body">
        <div className="hm-sub-health-ring">
          <svg viewBox="0 0 52 52" aria-hidden="true">
            <circle cx="26" cy="26" r="22" />
            <circle cx="26" cy="26" r="22" style={{ strokeDasharray: ring, "--hm-ring-offset": offset }} />
          </svg>
          <strong>{health.healthScore}</strong>
        </div>
        <div className="hm-sub-health-copy">
          <strong>{health.healthLabel === "Savings likely" ? "Good - savings likely" : health.healthLabel}</strong>
          <span>{health.lowUse.length || health.duplicates.length
            ? `${health.lowUse.length + health.duplicates.length} subscription${health.lowUse.length + health.duplicates.length === 1 ? "" : "s"} flagged for review.`
            : "Your subscription set looks steady."}</span>
        </div>
      </div>
      <div className="hm-sub-health-bars">
        {bars.map(([label, pct, tone]) => (
          <div key={label} className="hm-sub-health-bar">
            <span>{label}</span>
            <div><i className={`is-${tone}`} style={{ width: `${pct}%` }} /></div>
            <em>{pct}%</em>
          </div>
        ))}
      </div>
    </section>
  );
}

function SubscriptionAlertsPanel({ renewingSoon, health, alerts, subById }) {
  const rows = [];
  if (renewingSoon.length > 0) {
    rows.push({
      tone: "warn",
      icon: "🔔",
      title: `${renewingSoon.length} renewal${renewingSoon.length > 1 ? "s" : ""} this week`,
      detail: renewingSoon.map(s => `${s.name} in ${daysUntil(s.next_renewal_date)}d`).join(" · "),
    });
  }
  const overlap = health.duplicates[0] || health.crowdedCategories[0];
  if (overlap) {
    rows.push({
      tone: "warn",
      icon: "⚠",
      title: "Overlap detected",
      detail: overlap.items
        ? `${overlap.items.map(s => s.name).join(" + ")} may be duplicated.`
        : `${CATEGORY_META[overlap.category]?.label || "Similar"} has ${overlap.count} active subscriptions.`,
    });
  }
  const priceAlert = alerts.find(a => a.alert_type === "price_change");
  if (priceAlert) {
    rows.push({
      tone: "danger",
      icon: "↗",
      title: `${subById[priceAlert.subscription_id]?.name || "Subscription"} price changed`,
      detail: "Tap the subscription to review or negotiate.",
    });
  }
  if (!rows.length) return null;

  return (
    <section className="hm-sub-compact-alerts" aria-label="Subscription alerts">
      <div className="hm-sub-section-kicker">Alerts</div>
      {rows.slice(0, 3).map(row => (
        <div key={`${row.title}-${row.tone}`} className={`hm-sub-compact-alert is-${row.tone}`}>
          <span>{row.icon}</span>
          <div>
            <strong>{row.title}</strong>
            <small>{row.detail}</small>
          </div>
        </div>
      ))}
    </section>
  );
}

function SubscriptionCategoryPanel({ catTotals, totalMonthly }) {
  const entries = Object.entries(catTotals).filter(([, amount]) => amount > 0).sort((a, b) => b[1] - a[1]);
  const top = entries.slice(0, 4);
  const restTotal = entries.slice(4).reduce((sum, [, amount]) => sum + amount, 0);
  const rows = restTotal > 0.01 ? [...top, ["__rest__", restTotal]] : top;
  const max = Math.max(...rows.map(([, amount]) => amount), 1);

  return (
    <section className="hm-sub-category-panel" aria-label="Spend by category">
      <div className="hm-sub-section-kicker">Spend by category</div>
      <div className="hm-sub-category-card">
        {rows.length === 0 ? (
          <p className="hm-sub-category-empty">Add a subscription to see where your money goes.</p>
        ) : rows.map(([category, amount], index) => {
          const isRest = category === "__rest__";
          const meta = isRest ? null : (CATEGORY_META[category] || CATEGORY_META.other);
          const pct = totalMonthly > 0 ? Math.round((amount / totalMonthly) * 100) : 0;
          return (
            <div className="hm-sub-category-row" key={category}>
              <span>{isRest ? "Other categories" : `${meta.emoji} ${meta.label}`}</span>
              <div className="hm-sub-category-track">
                <i className={isRest ? "is-rest" : `is-cat-${index + 1}`} style={{ width: `${Math.max(6, (amount / max) * 100)}%` }} />
              </div>
              <b>{fmt$(amount)}</b>
              <em>{pct}%</em>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function SubscriptionIntelligencePanel({ health, subs, signals = [], onReport, onOpenSub }) {
  const active = subs.filter(s => s.status === "active" || s.status === "trial");
  const items = [];
  signals.slice(0, 2).forEach((finding, index) => {
    const sub = subs.find(s => s.id === finding.subscription_id);
    items.push({
      key: `signal-${finding.type || index}-${finding.subscription_id || index}`,
      sub,
      reportOnly: !sub,
      name: finding.title || "Household signal",
      detail: signalReason(finding),
      badge: signalSourceLabel(finding),
      tone: finding.priority === "high" ? "danger" : "overlap",
      amount: finding.estimated_monthly_savings || 0,
    });
  });
  health.lowUse.slice(0, 2).forEach(s => items.push({
    key: `low-${s.id}`,
    sub: s,
    name: s.name,
    detail: `${usageDetail(s)} ${CATEGORY_META[s.category]?.label || "Subscription"}`,
    badge: "Cancel",
    tone: "danger",
    amount: s.monthly_amount || 0,
  }));
  health.duplicates.slice(0, 1).forEach(d => {
    const first = d.items[0];
    items.push({
      key: `dup-${d.name}`,
      sub: first,
      name: first.name,
      detail: `Overlap with ${d.items.slice(1).map(s => s.name).join(" + ") || "similar subscription"}`,
      badge: "Overlap",
      tone: "overlap",
      amount: first.monthly_amount || 0,
    });
  });
  if (items.length < 3) {
    active
      .filter(s => !items.some(item => item.sub?.id === s.id))
      .slice(0, 3 - items.length)
      .forEach(s => items.push({
        key: `review-${s.id}`,
        sub: s,
        name: s.name,
        detail: s.usage_rating ? usageDetail(s) : "Review value before renewal",
        badge: s.usage_rating && Number(s.usage_rating) <= 3 ? "Downgrade" : "Review",
        tone: "save",
        amount: s.monthly_amount || 0,
      }));
  }

  return (
    <section className="hm-sub-intel-panel" aria-label="AI waste report">
      <div className="hm-sub-section-kicker">AI waste report</div>
      <div className="hm-sub-intel-card">
        <div className="hm-sub-intel-head">
          <strong>✦ Subscription intelligence</strong>
          <span>Updated today</span>
        </div>
        <div className="hm-sub-intel-list">
          {items.slice(0, 3).map(item => (
            <button key={item.key} type="button" onClick={() => item.sub ? onOpenSub(item.sub) : onReport()}>
              <span>
                <strong>{item.name}</strong>
                <small>{item.detail}</small>
              </span>
              <em className={`is-${item.tone}`}>{item.badge}</em>
              <b>{fmt$(item.amount)}</b>
            </button>
          ))}
        </div>
        <div className="hm-sub-intel-total">
          <span>Potential annual savings</span>
          <strong>+{fmtShort$(health.estimatedYearlySavings)}/yr</strong>
        </div>
      </div>
    </section>
  );
}

function SubscriptionInsightSheet({ sub, onClose, onView, onUpdated }) {
  useBackButton(onClose, Boolean(sub));
  const { applyAiUsage } = useAuth();
  const [usageLogs, setUsageLogs] = useState([]);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [scriptResult, setScriptResult] = useState(null);
  const [aiBusy, setAiBusy] = useState("");
  const [aiError, setAiError] = useState("");
  const [renewalBusy, setRenewalBusy] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragStartY = useRef(null);
  const dragOffsetRef = useRef(0);

  useEffect(() => {
    if (!sub) return;
    setDragOffset(0);
    setDragging(false);
    dragStartY.current = null;
    dragOffsetRef.current = 0;
    setLoadingUsage(true);
    setAiResult(null);
    setScriptResult(null);
    setAiError("");
    subscriptionsApi.getUsage(sub.id)
      .then(logs => setUsageLogs(Array.isArray(logs) ? logs : []))
      .catch(() => setUsageLogs([]))
      .finally(() => setLoadingUsage(false));
  }, [sub]);

  const recentUsage = useMemo(() => {
    const byDate = Object.fromEntries(usageLogs.map(l => [l.log_date, Boolean(l.used_today)]));
    return Array.from({ length: 7 }).map((_, idx) => {
      const d = new Date();
      d.setDate(d.getDate() - (6 - idx));
      const key = d.toISOString().split("T")[0];
      return {
        key,
        used: byDate[key],
        label: d.toLocaleDateString("en-US", { weekday: "short" }).slice(0, 1),
      };
    });
  }, [usageLogs]);

  if (!sub) return null;

  const cat = CATEGORY_META[sub.category] || CATEGORY_META.other;
  const sc = STATUS_CONFIG[sub.status] || STATUS_CONFIG.active;
  const usageRating = getUsageRating(sub);
  const lowUse = isLowUseSubscription(sub);
  const monthly = Number(sub.monthly_amount || 0);
  const aiScore = aiResult?.score || (usageRating ? Math.max(2, Math.min(10, usageRating * 2)) : null);
  const verdict = aiResult?.verdict || (lowUse ? "Review" : usageRating >= 4 ? "Keep" : "Score");
  const renewalDays = daysUntil(sub.next_renewal_date);
  const renewalDateIsStale = sub.status === "active" && renewalDays != null && renewalDays < 0;
  const renewalNeedsAction = sub.status === "active" && renewalDays != null && renewalDays <= 7;
  const advancedRenewalDate = renewalNeedsAction ? advanceRenewalDate(sub.next_renewal_date, sub.billing_cycle, true) : "";

  function closeWithSlide() {
    setDragging(false);
    setDragOffset(window.innerHeight);
    window.setTimeout(onClose, 160);
  }

  function beginDrag(clientY) {
    dragStartY.current = clientY;
    dragOffsetRef.current = 0;
    setDragging(true);
  }

  function updateDrag(clientY, event) {
    if (dragStartY.current == null) return;
    const nextOffset = Math.max(0, clientY - dragStartY.current);
    dragOffsetRef.current = nextOffset;
    setDragOffset(nextOffset);
    if (nextOffset > 4) event?.preventDefault?.();
  }

  function finishDrag() {
    if (dragStartY.current == null) return;
    dragStartY.current = null;
    if (dragOffsetRef.current > 42) {
      closeWithSlide();
      return;
    }
    dragOffsetRef.current = 0;
    setDragging(false);
    setDragOffset(0);
  }

  async function toggleUsage(day) {
    setLoadingUsage(true);
    try {
      const saved = await subscriptionsApi.logUsage(sub.id, { log_date: day.key, used_today: !day.used });
      setUsageLogs(prev => {
        const without = prev.filter(l => l.log_date !== saved.log_date);
        return [saved, ...without];
      });
    } catch {
      setAiError("Could not update usage.");
    } finally {
      setLoadingUsage(false);
    }
  }

  async function markRenewed() {
    if (!advancedRenewalDate) return;
    setRenewalBusy(true);
    setAiError("");
    try {
      const updated = await subscriptionsApi.update(sub.id, { next_renewal_date: advancedRenewalDate });
      onUpdated?.(updated);
    } catch (ex) {
      setAiError(ex.message || "Could not update renewal date.");
    } finally {
      setRenewalBusy(false);
    }
  }

  async function runSheetAi(action) {
    setAiBusy(action);
    setAiError("");
    try {
      if (action === "score") {
        const result = await subscriptionsApi.keepScore(sub.id);
        if (result.error === "ai_not_configured") { setAiError("AI is not configured yet."); return; }
        if (result.error === "ai_limit_reached") { setAiError(result.message || "AI request limit reached."); return; }
        applyAiUsage?.(result.ai_usage);
        setAiResult(result);
        setScriptResult(null);
      } else {
        const result = await subscriptionsApi.cancelScript(sub.id, { action, reason: "" });
        if (result.error === "ai_not_configured") { setAiError("AI is not configured yet."); return; }
        if (result.error === "ai_limit_reached") { setAiError(result.message || "AI request limit reached."); return; }
        applyAiUsage?.(result.ai_usage);
        setScriptResult({ action, ...result });
      }
    } catch (ex) {
      setAiError(ex.message || "Could not run subscription AI.");
    } finally {
      setAiBusy("");
    }
  }

  return (
    <div className="hm-util-drawer-shell hm-sub-mobile-sheet-shell hm-sub-insight-sheet-shell" role="dialog" aria-modal="true">
      <button className="hm-util-drawer-scrim hm-sub-mobile-sheet-scrim" type="button" onClick={closeWithSlide} aria-label="Close subscription insights" />
      <aside
        className={`hm-util-drawer hm-sub-mobile-sheet hm-sub-insight-sheet${dragging ? " is-dragging" : ""}`}
        style={{ transform: dragOffset ? `translateY(${dragOffset}px)` : undefined }}
        onClick={event => event.stopPropagation()}
      >
        <button
          type="button"
          className="hm-sheet-handle"
          onPointerDown={event => {
            if (event.pointerType === "mouse" && event.button !== 0) return;
            event.currentTarget.setPointerCapture?.(event.pointerId);
            beginDrag(event.clientY);
          }}
          onPointerMove={event => updateDrag(event.clientY, event)}
          onPointerUp={event => {
            event.currentTarget.releasePointerCapture?.(event.pointerId);
            finishDrag();
          }}
          onPointerCancel={event => {
            event.currentTarget.releasePointerCapture?.(event.pointerId);
            finishDrag();
          }}
          aria-label="Drag down to close subscription insights"
        />
        <SheetLogoRow title="Subscription insights" onClose={closeWithSlide} closeLabel="Close subscription insights" />
        <div className="hm-sub-mobile-sheet-head">
          <span>{sub.logo_emoji || cat.emoji}</span>
          <div>
            <strong>{sub.name}</strong>
            <small>{fmt$(sub.monthly_amount)}/mo · {cat.label} · {sc.label}</small>
          </div>
        </div>
        <div className="hm-sub-card-expanded">
          <div className="hm-sub-card-usage">
            <div className="hm-sub-card-section-title">
              <span>7-day usage</span>
              {loadingUsage && <em>Saving...</em>}
            </div>
            <div className="hm-sub-card-usage-dots hm-sub-card-usage-dots--tap" aria-label="Tap to mark subscription usage">
              {recentUsage.map(day => (
                <button
                  key={day.key}
                  type="button"
                  className={`hm-sub-card-usage-dot${day.used ? " is-used" : ""}`}
                  onClick={() => toggleUsage(day)}
                  aria-pressed={Boolean(day.used)}
                  title={day.key}
                >
                  <span>{day.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="hm-sub-card-mini-grid">
            <div><span>Annual cost</span><strong>{fmt$(monthly * 12)}</strong></div>
            <div><span>Renewal</span><strong>{sub.next_renewal_date ? fmtDate(sub.next_renewal_date) : "Missing"}</strong></div>
            <div><span>AI score</span><strong>{aiScore ? `${aiScore}/10` : "Run"}</strong></div>
            <div><span>Verdict</span><strong>{verdict}</strong></div>
          </div>
          {renewalNeedsAction && (
            <div className="hm-sub-renewal-maintenance">
              <span>
                {renewalDateIsStale
                  ? `Renewal date passed. If it renewed, move the next date to ${fmtDate(advancedRenewalDate)}.`
                  : `If this renewal is handled, move the next date to ${fmtDate(advancedRenewalDate)}.`}
              </span>
              <button type="button" onClick={markRenewed} disabled={renewalBusy}>
                {renewalBusy ? "Updating..." : "Mark renewed"}
              </button>
            </div>
          )}
          <div className="hm-sub-card-ai-actions">
            <button type="button" onClick={() => runSheetAi("score")} disabled={Boolean(aiBusy)}>
              {aiBusy === "score" ? "Scoring..." : "Score it"}
            </button>
            <button type="button" onClick={() => runSheetAi("negotiate")} disabled={Boolean(aiBusy)}>
              {aiBusy === "negotiate" ? "Writing..." : "Negotiate"}
            </button>
            <button type="button" className="is-danger" onClick={() => runSheetAi("cancel")} disabled={Boolean(aiBusy)}>
              {aiBusy === "cancel" ? "Writing..." : "Cancel script"}
            </button>
          </div>
          {(aiResult || scriptResult || aiError) && (
            <div className="hm-sub-card-ai-result">
              {aiError && <span className="is-error">{aiError}</span>}
              {aiResult && !aiError && (
                <span><strong>{aiResult.verdict || "Score"} · {aiResult.score || "?"}/10</strong>{aiResult.reasoning ? ` ${aiResult.reasoning}` : ""}</span>
              )}
              {scriptResult && !aiError && (
                <span><strong>{scriptResult.action === "cancel" ? "Cancel script ready" : "Negotiation script ready"}</strong>{scriptResult.script?.opening ? ` "${scriptResult.script.opening}"` : ""}</span>
              )}
            </div>
          )}
          <button type="button" className="hm-sub-card-detail-link" onClick={onView}>
            Open full details →
          </button>
        </div>
      </aside>
    </div>
  );
}

function SubscriptionMobileSheet({ sub, onClose, onView, onEdit, onToggleCancel, onDelete }) {
  const [dragOffset, setDragOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragStartY = useRef(null);
  const dragOffsetRef = useRef(0);

  useEffect(() => {
    if (!sub) return;
    setDragOffset(0);
    setDragging(false);
    dragStartY.current = null;
    dragOffsetRef.current = 0;
  }, [sub]);

  if (!sub) return null;

  const cat = CATEGORY_META[sub.category] || CATEGORY_META.other;
  const status = STATUS_CONFIG[sub.status]?.label || sub.status;

  function closeWithSlide() {
    setDragging(false);
    setDragOffset(window.innerHeight);
    window.setTimeout(onClose, 160);
  }

  function beginDrag(clientY) {
    dragStartY.current = clientY;
    dragOffsetRef.current = 0;
    setDragging(true);
  }

  function updateDrag(clientY, event) {
    if (dragStartY.current == null) return;
    const nextOffset = Math.max(0, clientY - dragStartY.current);
    dragOffsetRef.current = nextOffset;
    setDragOffset(nextOffset);
    if (nextOffset > 4) event?.preventDefault?.();
  }

  function finishDrag() {
    if (dragStartY.current == null) return;
    dragStartY.current = null;
    if (dragOffsetRef.current > 42) {
      closeWithSlide();
      return;
    }
    dragOffsetRef.current = 0;
    setDragging(false);
    setDragOffset(0);
  }

  return (
    <div className="hm-util-drawer-shell hm-sub-mobile-sheet-shell hm-sub-action-sheet-shell" role="dialog" aria-modal="true">
      <button className="hm-util-drawer-scrim hm-sub-mobile-sheet-scrim" type="button" onClick={closeWithSlide} aria-label="Close subscription actions" />
      <aside
        className={`hm-util-drawer hm-sub-mobile-sheet hm-sub-action-sheet${dragging ? " is-dragging" : ""}`}
        style={{ transform: dragOffset ? `translateY(${dragOffset}px)` : undefined }}
        onClick={event => event.stopPropagation()}
      >
        <button
          type="button"
          className="hm-sheet-handle"
          onPointerDown={event => {
            if (event.pointerType === "mouse" && event.button !== 0) return;
            event.currentTarget.setPointerCapture?.(event.pointerId);
            beginDrag(event.clientY);
          }}
          onPointerMove={event => updateDrag(event.clientY, event)}
          onPointerUp={event => {
            event.currentTarget.releasePointerCapture?.(event.pointerId);
            finishDrag();
          }}
          onPointerCancel={event => {
            event.currentTarget.releasePointerCapture?.(event.pointerId);
            finishDrag();
          }}
          aria-label="Drag down to close subscription actions"
        />
        <SheetLogoRow title="Subscription actions" onClose={closeWithSlide} closeLabel="Close subscription actions" />
        <div className="hm-sub-mobile-sheet-head">
          <span>{sub.logo_emoji || cat.emoji}</span>
          <div>
            <strong>{sub.name}</strong>
            <small>{fmt$(sub.monthly_amount)}/mo · {cat.label} · {status}</small>
          </div>
        </div>
        <div className="hm-sub-action-sheet-group">
          <span>Review</span>
          <button type="button" onClick={onView}>
            <span>↗</span>
            <div>
              <strong>View insights and details</strong>
              <small>Usage, score, history, and scripts</small>
            </div>
            <em>›</em>
          </button>
        </div>
        <div className="hm-sub-action-sheet-group">
          <span>Manage</span>
          <button type="button" onClick={onEdit}>
            <span>✏️</span>
            <div>
              <strong>Edit subscription</strong>
              <small>Renewal, price, category, and notes</small>
            </div>
            <em>›</em>
          </button>
          {onToggleCancel && (
            <button type="button" onClick={() => onToggleCancel(sub, sub.status !== "cancelled")}>
              <span>{sub.status === "cancelled" ? "🔁" : "🚫"}</span>
              <div>
                <strong>{sub.status === "cancelled" ? "Reactivate subscription" : "Cancel subscription"}</strong>
                <small>{sub.status === "cancelled" ? "Mark it active again" : "Mark cancelled and keep its history"}</small>
              </div>
              <em>›</em>
            </button>
          )}
          <button type="button" className="is-danger" onClick={onDelete}>
            <span>🗑️</span>
            <div>
              <strong>Remove subscription</strong>
              <small>Delete it from HouseMind</small>
            </div>
            <em>›</em>
          </button>
        </div>
      </aside>
    </div>
  );
}

function ConfirmRemoveSubscription({ sub, onCancel, onConfirm }) {
  if (!sub) return null;
  return (
    <ConfirmModal
      tag="⚠ Remove subscription"
      title="Remove subscription?"
      message={`${sub.name} will be removed from your subscription list. You'll have a few seconds to undo.`}
      confirmLabel="Remove"
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );
}

function SubscriptionsAskBar() {
  const prompts = [
    "What should I cancel or review?",
    "Where can my household consolidate?",
    "What renewals are coming soon?",
  ];

  function openSubscriptionsAsk(question = "") {
    const clean = String(question || "").trim();
    const contextualQuestion = clean
      ? `${clean} Focus on subscriptions.`
      : "Ask about my subscriptions.";
    window.dispatchEvent(new CustomEvent("hm_open_ask", {
      detail: {
        question: contextualQuestion,
        context: {
          module: "subscriptions",
          module_label: "Subscriptions",
          surface: "Subscriptions",
        },
      },
    }));
  }

  return (
    <div className="fd-food-askbar hm-sub-askbar">
      <input
        className="fd-food-askbar__input"
        placeholder="✦ Ask about subscriptions..."
        onFocus={() => openSubscriptionsAsk("")}
        readOnly
      />
      <div className="fd-food-askbar__chips">
        {prompts.map(prompt => (
          <button
            key={prompt}
            type="button"
            className="fd-food-askbar__chip"
            onClick={() => openSubscriptionsAsk(prompt)}
          >
            {prompt}
          </button>
        ))}
      </div>
      <button type="button" className="fd-food-askbar__button" onClick={() => openSubscriptionsAsk(prompts[0])}>
        ✦ Ask
      </button>
    </div>
  );
}

// ── Quick-add panel ───────────────────────────────────────────────────────────

function QuickAddPanel({ onSaved, onClose }) {
  const [activeTab, setActiveTab] = useState("streaming");
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ ...BLANK_FORM });
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [dragOffset, setDragOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragStartY = useRef(null);
  const dragOffsetRef = useRef(0);
  const presentation = useFormPresentation();
  const showSaving = useMinLoad(busy, 700);
  const cats = Object.keys(QUICK_ADD);

  function chooseItem(item, category = activeTab) {
    const next = {
      ...BLANK_FORM,
      name: item.name,
      logo_emoji: item.emoji,
      category,
      amount: item.amount || "",
      billing_cycle: "monthly",
      website_url: item.url ? `https://${item.url}` : "",
    };
    setSelected({ ...item, category });
    setForm(next);
    setErr("");
  }

  function setField(key, value) {
    setForm(prev => {
      const next = { ...prev, [key]: value };
      if (key === "status" && value !== "trial") next.trial_end_date = "";
      if (key === "is_shared") next.shared_member_count = value ? (prev.shared_member_count || 2) : 2;
      return next;
    });
  }

  function nextStep() {
    if (step === 1 && !selected) {
      setErr("Choose a subscription to continue.");
      return;
    }
    if (step === 2 && !form.name.trim()) {
      setErr("Name is required.");
      return;
    }
    setErr("");
    setStep(prev => Math.min(3, prev + 1));
  }

  async function save() {
    if (!form.name.trim()) {
      setStep(2);
      setErr("Name is required.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const result = await subscriptionsApi.create({
        ...form,
        amount: form.amount ? parseFloat(form.amount) : null,
        usage_rating: form.usage_rating ? parseInt(form.usage_rating) : null,
        shared_member_count: form.is_shared ? parseInt(form.shared_member_count || 2) : null,
      });
      onSaved(result);
    } catch (ex) {
      setErr(ex.message || "Could not save subscription.");
    } finally {
      setBusy(false);
    }
  }

  function closeWithSlide() {
    setDragging(false);
    setDragOffset(window.innerHeight);
    window.setTimeout(onClose, 160);
  }

  function beginDrag(clientY) {
    dragStartY.current = clientY;
    dragOffsetRef.current = 0;
    setDragging(true);
  }

  function updateDrag(clientY, event) {
    if (dragStartY.current == null) return;
    const nextOffset = Math.max(0, clientY - dragStartY.current);
    dragOffsetRef.current = nextOffset;
    setDragOffset(nextOffset);
    if (nextOffset > 4) event?.preventDefault?.();
  }

  function finishDrag() {
    if (dragStartY.current == null) return;
    dragStartY.current = null;
    if (dragOffsetRef.current > 42) {
      closeWithSlide();
      return;
    }
    dragOffsetRef.current = 0;
    setDragging(false);
    setDragOffset(0);
  }

  const drawerContent = (
    <>
      {showSaving && <SavingNotice title="Saving" message="Adding subscription..." />}
      <button
        type="button"
        className="hm-sheet-handle"
        onPointerDown={event => {
          if (event.pointerType === "mouse" && event.button !== 0) return;
          event.currentTarget.setPointerCapture?.(event.pointerId);
          beginDrag(event.clientY);
        }}
        onPointerMove={event => updateDrag(event.clientY, event)}
        onPointerUp={event => {
          event.currentTarget.releasePointerCapture?.(event.pointerId);
          finishDrag();
        }}
        onPointerCancel={event => {
          event.currentTarget.releasePointerCapture?.(event.pointerId);
          finishDrag();
        }}
        aria-label="Drag down to close quick add"
      />
      <SheetLogoRow title="Quick add subscription" onClose={closeWithSlide} closeLabel="Close quick add" />
      <div className="hm-util-step-indicator" aria-label={`Step ${step} of 3`}>
        {[1, 2, 3].map(item => <span key={item} className={item === step ? "is-active" : item < step ? "is-done" : ""} />)}
        <em>Step {step} of 3</em>
      </div>

      {err && <div className="hm-inline-error">{err}</div>}

      {step === 1 && (
        <div className="hm-sub-quickadd">
          <div className="hm-sub-qa-tabs">
            {cats.map(c => (
              <button key={c} type="button"
                className={`hm-sub-qa-tab${activeTab === c ? " is-active" : ""}`}
                onClick={() => setActiveTab(c)}>
                {QUICK_ADD_CATEGORY_LABELS[c] || CATEGORY_META[c]?.label}
              </button>
            ))}
          </div>
          <div className="hm-sub-qa-grid">
            {(QUICK_ADD[activeTab] || []).map(item => (
              <button key={item.name} type="button"
                className={`hm-sub-qa-item${selected?.name === item.name && selected?.category === activeTab ? " is-selected" : ""}`}
                onClick={() => chooseItem(item, activeTab)}>
                <span className="hm-sub-qa-emoji">{item.emoji}</span>
                <span className="hm-sub-qa-name">{item.name}</span>
                {item.amount > 0 && <span className="hm-sub-qa-price">${item.amount}/mo</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="hm-util-drawer-form">
          <label className="hm-util-ledger-span">
            <span>Name</span>
            <input className="hm-auth-input" value={form.name} onChange={e => setField("name", e.target.value)} />
          </label>
          <label>
            <span>Amount</span>
            <input className="hm-auth-input" type="number" min="0" step="0.01" value={form.amount} onChange={e => setField("amount", e.target.value)} placeholder="9.99" />
          </label>
          <label>
            <span>Cycle</span>
            <select className="hm-auth-input" value={form.billing_cycle} onChange={e => setField("billing_cycle", e.target.value)}>
              {BILLING_CYCLES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </label>
          <label>
            <span>Category</span>
            <select className="hm-auth-input" value={form.category} onChange={e => setField("category", e.target.value)}>
              {Object.entries(CATEGORY_META).map(([key, meta]) => <option key={key} value={key}>{meta.emoji} {meta.label}</option>)}
            </select>
          </label>
          <label className="hm-util-ledger-span">
            <span>Website</span>
            <input className="hm-auth-input" value={form.website_url} onChange={e => setField("website_url", e.target.value)} placeholder="https://netflix.com" />
          </label>
        </div>
      )}

      {step === 3 && (
        <div className="hm-util-drawer-form">
          <div className="hm-util-form-section hm-util-ledger-span">
            <strong>Renewal and value</strong>
            <span>Add the details HouseMind needs for renewal alerts and waste checks.</span>
          </div>
          <label>
            <span>Renewal date</span>
            <input className="hm-auth-input" type="date" value={form.next_renewal_date} onChange={e => setField("next_renewal_date", e.target.value)} />
          </label>
          <label>
            <span>Status</span>
            <select className="hm-auth-input" value={form.status} onChange={e => setField("status", e.target.value)}>
              {Object.entries(STATUS_CONFIG).map(([value, config]) => <option key={value} value={value}>{config.label}</option>)}
            </select>
          </label>
          {form.status === "trial" && (
            <label className="hm-util-ledger-span">
              <span>Trial ends</span>
              <input className="hm-auth-input" type="date" value={form.trial_end_date} onChange={e => setField("trial_end_date", e.target.value)} />
            </label>
          )}
          <label>
            <span>Value rating</span>
            <select className="hm-auth-input" value={form.usage_rating} onChange={e => setField("usage_rating", e.target.value)}>
              <option value="">Not rated</option>
              <option value="1">1 - Rarely use it</option>
              <option value="2">2 - Use occasionally</option>
              <option value="3">3 - Use regularly</option>
              <option value="4">4 - Use a lot</option>
              <option value="5">5 - Can't live without</option>
            </select>
          </label>
          <label>
            <span>Payment method</span>
            <input className="hm-auth-input" value={form.payment_method} onChange={e => setField("payment_method", e.target.value)} placeholder="Visa 4242" />
          </label>
          <div className="hm-util-ledger-span hm-sub-shared-control">
            <span>Shared plan</span>
            <div>
              <label className="hm-util-checkbox">
                <input type="checkbox" checked={Boolean(form.is_shared)} onChange={e => setField("is_shared", e.target.checked)} />
                <strong>Split this cost with household members</strong>
              </label>
              {form.is_shared && (
                <input className="hm-auth-input" type="number" min="2" max="20" step="1"
                  value={form.shared_member_count || 2}
                  onChange={e => setField("shared_member_count", e.target.value)}
                  aria-label="People sharing this subscription" />
              )}
            </div>
          </div>
          <label className="hm-util-ledger-span">
            <span>Notes</span>
            <textarea className="hm-auth-input" rows={2} value={form.notes} onChange={e => setField("notes", e.target.value)} />
          </label>
        </div>
      )}

      <div className="hm-util-drawer-footer">
        {step > 1 ? (
          <button type="button" className="hm-util-back-link" onClick={() => { setErr(""); setStep(step - 1); }}>← Back</button>
        ) : (
          <button type="button" className="hm-util-back-link" onClick={closeWithSlide}>Cancel</button>
        )}
        {step < 3 ? (
          <button type="button" onClick={nextStep}>Next</button>
        ) : (
          <button type="button" className="is-primary" disabled={busy} onClick={save}>{busy ? "Saving..." : "Add subscription"}</button>
        )}
      </div>
    </>
  );

  if (presentation === "modal") {
    return (
      <CenteredModal title="Quick add subscription" onClose={onClose} width={620}>
        {drawerContent}
      </CenteredModal>
    );
  }

  return (
    <div className="hm-util-drawer-shell hm-sub-quickadd-shell" role="dialog" aria-modal="true">
      <button className="hm-util-drawer-scrim" type="button" onClick={closeWithSlide} aria-label="Close quick add" />
      <aside
        className={`hm-util-drawer hm-sub-add-drawer hm-sub-quickadd-drawer${dragging ? " is-dragging" : ""}`}
        style={{ transform: dragOffset ? `translateY(${dragOffset}px)` : undefined }}
      >
        {drawerContent}
      </aside>
    </div>
  );
}

// ── Add / Edit form ───────────────────────────────────────────────────────────

function SubForm({ initial, onSaved, onCancel, title }) {
  const [form, setForm] = useState(initial || BLANK_FORM);
  const isEditing = Boolean(initial?.id);
  const [step, setStep] = useState(initial?.name ? 2 : 1);
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState("");
  const [dragOffset, setDragOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragStartY = useRef(null);
  const dragOffsetRef = useRef(0);
  const presentation = useFormPresentation();
  const showSaving = useMinLoad(busy, 700);

  function setField(k, v) {
    setForm(f => {
      const next = { ...f, [k]: v };
      if (k === "status" && v !== "trial") next.trial_end_date = "";
      if (k === "is_shared") next.shared_member_count = v ? (f.shared_member_count || 2) : 2;
      return next;
    });
  }

  function chooseCategory(category) {
    setForm(f => ({ ...f, category }));
    window.setTimeout(() => setStep(2), 180);
  }

  function nextStep() {
    if (step === 2 && !form.name.trim()) {
      setErr("Name is required.");
      return;
    }
    setErr("");
    setStep(s => Math.min(3, s + 1));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setErr("Name is required."); return; }
    setBusy(true); setErr("");
    try {
      const payload = {
        ...form,
        amount:       form.amount     ? parseFloat(form.amount)     : null,
        usage_rating: form.usage_rating ? parseInt(form.usage_rating) : null,
        shared_member_count: form.is_shared ? parseInt(form.shared_member_count || 2) : null,
      };
      const result = initial?.id
        ? await subscriptionsApi.update(initial.id, payload)
        : await subscriptionsApi.create(payload);
      onSaved(result);
    } catch (ex) {
      setErr(ex.message || "Could not save");
    } finally {
      setBusy(false);
    }
  }

  function closeWithSlide() {
    setDragging(false);
    setDragOffset(window.innerHeight);
    window.setTimeout(onCancel, 160);
  }

  function beginDrag(clientY) {
    dragStartY.current = clientY;
    dragOffsetRef.current = 0;
    setDragging(true);
  }

  function updateDrag(clientY, event) {
    if (dragStartY.current == null) return;
    const nextOffset = Math.max(0, clientY - dragStartY.current);
    dragOffsetRef.current = nextOffset;
    setDragOffset(nextOffset);
    if (nextOffset > 4) event?.preventDefault?.();
  }

  function finishDrag() {
    if (dragStartY.current == null) return;
    dragStartY.current = null;
    if (dragOffsetRef.current > 42) {
      closeWithSlide();
      return;
    }
    dragOffsetRef.current = 0;
    setDragging(false);
    setDragOffset(0);
  }

  const formContent = (
    <>
        {showSaving && <SavingNotice title="Saving" message="Saving subscription..." />}
        <button
          type="button"
          className="hm-sheet-handle"
          onPointerDown={event => {
            if (event.pointerType === "mouse" && event.button !== 0) return;
            event.currentTarget.setPointerCapture?.(event.pointerId);
            beginDrag(event.clientY);
          }}
          onPointerMove={event => updateDrag(event.clientY, event)}
          onPointerUp={event => {
            event.currentTarget.releasePointerCapture?.(event.pointerId);
            finishDrag();
          }}
          onPointerCancel={event => {
            event.currentTarget.releasePointerCapture?.(event.pointerId);
            finishDrag();
          }}
          aria-label="Drag down to close subscription form"
        />
        <SheetLogoRow title={title || "Add subscription"} onClose={closeWithSlide} closeLabel="Close subscription form" />
        {isEditing && (
          <div className="hm-util-step-indicator" aria-label={`Step ${step} of 3`}>
            {[1, 2, 3].map(item => <span key={item} className={item === step ? "is-active" : item < step ? "is-done" : ""} />)}
            <em>Step {step} of 3</em>
          </div>
        )}

        {err && <div className="hm-inline-error">{err}</div>}

        {!isEditing ? (
          <form onSubmit={handleSubmit} className="hm-sub-manual-compact-form">
            <div className="hm-sub-manual-required">
              <label className="hm-util-ledger-span">
                <span>Name</span>
                <ProviderNameInput
                  value={form.name}
                  onChange={v => setField("name", v)}
                  onSelect={p => {
                    setField("name", p.name);
                    if (!form.amount) setField("amount", String(p.amount));
                    if (p.url)      setField("website_url", p.url);
                    if (p.emoji)    setField("logo_emoji", p.emoji);
                    if (p.category) setField("category", p.category);
                  }}
                />
              </label>
              <label>
                <span>Amount</span>
                <input className="hm-auth-input" type="number" step="0.01" min="0" placeholder="9.99"
                  value={form.amount} onChange={e => setField("amount", e.target.value)} />
              </label>
              <label>
                <span>Cycle</span>
                <select className="hm-auth-input" value={form.billing_cycle}
                  onChange={e => setField("billing_cycle", e.target.value)}>
                  {BILLING_CYCLES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </label>
              <label className="hm-util-ledger-span">
                <span>Category</span>
                <select className="hm-auth-input" value={form.category}
                  onChange={e => setField("category", e.target.value)}>
                  {Object.entries(CATEGORY_META).map(([key, meta]) => (
                    <option key={key} value={key}>{meta.emoji} {meta.label}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="hm-sub-manual-optional">
            <div className="hm-util-form-section hm-util-ledger-span">
              <strong>Optional details</strong>
              <span>Add renewal, payment, and notes if you have them.</span>
            </div>
            {form.is_shared && form.amount && Number(form.shared_member_count || 0) > 1 && (
              <div className="hm-sub-manual-split-preview hm-util-ledger-span">
                <span>Shared value</span>
                <strong>{fmt$(Number(form.amount || 0) / Number(form.shared_member_count || 2))} each</strong>
              </div>
            )}
              <label>
                <span>Renewal date</span>
                <input className="hm-auth-input" type="date"
                  value={form.next_renewal_date} onChange={e => setField("next_renewal_date", e.target.value)} />
              </label>
              <label>
                <span>Status</span>
                <select className="hm-auth-input" value={form.status}
                  onChange={e => setField("status", e.target.value)}>
                  {Object.entries(STATUS_CONFIG).map(([v, c]) => (
                    <option key={v} value={v}>{c.label}</option>
                  ))}
                </select>
              </label>
              {form.status === "trial" && (
                <label className="hm-util-ledger-span">
                  <span>Trial ends</span>
                  <input className="hm-auth-input" type="date"
                    value={form.trial_end_date} onChange={e => setField("trial_end_date", e.target.value)} />
                </label>
              )}
              <label>
                <span>Website</span>
                <input className="hm-auth-input" placeholder="netflix.com"
                  value={form.website_url} onChange={e => setField("website_url", e.target.value)} />
              </label>
              <label>
                <span>Payment</span>
                <input className="hm-auth-input" placeholder="Visa 4242"
                  value={form.payment_method} onChange={e => setField("payment_method", e.target.value)} />
              </label>
              <label>
                <span>Value rating</span>
                <select className="hm-auth-input" value={form.usage_rating}
                  onChange={e => setField("usage_rating", e.target.value)}>
                  <option value="">Not rated</option>
                  <option value="1">1 - Rarely use it</option>
                  <option value="2">2 - Use occasionally</option>
                  <option value="3">3 - Use regularly</option>
                  <option value="4">4 - Use a lot</option>
                  <option value="5">5 - Can't live without</option>
                </select>
              </label>
              <div className="hm-util-ledger-span hm-sub-shared-control">
                <span>Shared plan</span>
                <div>
                  <label className="hm-util-checkbox">
                    <input type="checkbox" checked={Boolean(form.is_shared)} onChange={e => setField("is_shared", e.target.checked)} />
                    <strong>Split this cost with household members</strong>
                  </label>
                  {form.is_shared && (
                    <input className="hm-auth-input" type="number" min="2" max="20" step="1"
                      value={form.shared_member_count || 2}
                      onChange={e => setField("shared_member_count", e.target.value)}
                      aria-label="People sharing this subscription" />
                  )}
                </div>
              </div>
              <label>
                <span>Emoji icon</span>
                <input className="hm-auth-input" placeholder="🎬" maxLength={2}
                  value={form.logo_emoji} onChange={e => setField("logo_emoji", e.target.value)} />
              </label>
              <label className="hm-util-ledger-span hm-sub-notes-field">
                <span>Notes optional</span>
                <textarea className="hm-auth-input" rows={2}
                  value={form.notes} onChange={e => setField("notes", e.target.value)} />
              </label>
            </div>
          </form>
        ) : (
        <form onSubmit={handleSubmit} className="hm-sub-drawer-body">
          {step === 1 && (
            <div className="hm-util-type-picker hm-sub-category-picker">
              {Object.entries(CATEGORY_META).map(([key, meta]) => (
                <button key={key} type="button" className={form.category === key ? "is-selected" : ""} onClick={() => chooseCategory(key)}>
                  <span>{meta.emoji}</span>
                  <strong>{meta.label}</strong>
                </button>
              ))}
            </div>
          )}

          {step === 2 && (
            <div className="hm-util-drawer-form">
              <label className="hm-util-ledger-span">
                <span>Name</span>
                <ProviderNameInput
                  value={form.name}
                  onChange={v => setField("name", v)}
                  onSelect={p => {
                    setField("name", p.name);
                    if (!form.amount) setField("amount", String(p.amount));
                    if (p.url)      setField("website_url", p.url);
                    if (p.emoji)    setField("logo_emoji", p.emoji);
                    if (p.category) setField("category", p.category);
                  }}
                />
              </label>
              <label>
                <span>Amount</span>
                <input className="hm-auth-input" type="number" step="0.01" min="0" placeholder="9.99"
                  value={form.amount} onChange={e => setField("amount", e.target.value)} />
              </label>
              <label>
                <span>Cycle</span>
                <select className="hm-auth-input" value={form.billing_cycle}
                  onChange={e => setField("billing_cycle", e.target.value)}>
                  {BILLING_CYCLES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </label>
              <label>
                <span>Renewal date</span>
                <input className="hm-auth-input" type="date"
                  value={form.next_renewal_date} onChange={e => setField("next_renewal_date", e.target.value)} />
              </label>
              <label>
                <span>Status</span>
                <select className="hm-auth-input" value={form.status}
                  onChange={e => setField("status", e.target.value)}>
                  {Object.entries(STATUS_CONFIG).map(([v, c]) => (
                    <option key={v} value={v}>{c.label}</option>
                  ))}
                </select>
              </label>
            </div>
          )}

          {step === 3 && (
            <div className="hm-util-drawer-form">
              <div className="hm-util-form-section hm-util-ledger-span">
                <strong>Optional details</strong>
                <span>Add only what helps you recognize or manage this subscription.</span>
              </div>
              {form.status === "trial" && (
                <label>
                  <span>Trial ends</span>
                  <input className="hm-auth-input" type="date"
                    value={form.trial_end_date} onChange={e => setField("trial_end_date", e.target.value)} />
                </label>
              )}
            <label>
              <span>Start date</span>
              <input className="hm-auth-input" type="date"
                value={form.start_date} onChange={e => setField("start_date", e.target.value)} />
            </label>

            <label>
              <span>Website</span>
              <input className="hm-auth-input" placeholder="netflix.com"
                value={form.website_url} onChange={e => setField("website_url", e.target.value)} />
            </label>

            <label>
              <span>Emoji icon</span>
              <input className="hm-auth-input" placeholder="🎬" maxLength={2}
                value={form.logo_emoji} onChange={e => setField("logo_emoji", e.target.value)} />
            </label>

            <label>
              <span>Payment method</span>
              <input className="hm-auth-input" placeholder="Visa 4242"
                value={form.payment_method} onChange={e => setField("payment_method", e.target.value)} />
            </label>

            <label>
              <span>My value rating</span>
              <select className="hm-auth-input" value={form.usage_rating}
                onChange={e => setField("usage_rating", e.target.value)}>
                <option value="">Not rated</option>
                <option value="1">1 - Rarely use it</option>
                <option value="2">2 - Use occasionally</option>
                <option value="3">3 - Use regularly</option>
                <option value="4">4 - Use a lot</option>
                <option value="5">5 - Can't live without</option>
              </select>
            </label>

            <div className="hm-util-ledger-span hm-sub-shared-control">
              <span>Shared plan</span>
              <div>
                <label className="hm-util-checkbox">
                  <input type="checkbox" checked={Boolean(form.is_shared)} onChange={e => setField("is_shared", e.target.checked)} />
                  <strong>Split this cost with household members</strong>
                </label>
                {form.is_shared && (
                  <input className="hm-auth-input" type="number" min="2" max="20" step="1"
                    value={form.shared_member_count || 2}
                    onChange={e => setField("shared_member_count", e.target.value)}
                    aria-label="People sharing this subscription" />
                )}
              </div>
            </div>

            <label className="hm-util-ledger-span hm-sub-notes-field">
              <span>Notes optional</span>
              <textarea className="hm-auth-input" rows={2}
                value={form.notes} onChange={e => setField("notes", e.target.value)} />
            </label>
            </div>
          )}
        </form>
        )}

        <div className="hm-util-drawer-footer">
          {isEditing && step > 1 ? (
            <button type="button" className="hm-util-back-link" onClick={() => { setErr(""); setStep(step - 1); }}>← Back</button>
          ) : (
            <button type="button" className="hm-util-back-link" onClick={closeWithSlide}>Cancel</button>
          )}
          {isEditing && step < 3 ? (
            <button type="button" onClick={nextStep}>Next</button>
          ) : (
            <button type="button" className="is-primary" disabled={busy} onClick={handleSubmit}>
              {busy ? "Saving..." : (initial?.id ? "Save changes" : "Add subscription")}
            </button>
          )}
        </div>
    </>
  );

  if (presentation === "modal") {
    return (
      <CenteredModal title={title || "Add subscription"} onClose={onCancel}>
        {formContent}
      </CenteredModal>
    );
  }

  return (
    <div className="hm-util-drawer-shell hm-sub-form-shell" role="dialog" aria-modal="true">
      <button className="hm-util-drawer-scrim" type="button" onClick={closeWithSlide} aria-label="Close subscription form" />
      <aside
        className={`hm-util-drawer hm-sub-add-drawer hm-sub-form-drawer${!isEditing ? " hm-sub-form-drawer--manual" : ""}${dragging ? " is-dragging" : ""}`}
        style={{ transform: dragOffset ? `translateY(${dragOffset}px)` : undefined }}
      >
        {formContent}
      </aside>
    </div>
  );
}

// ── Subscription card ─────────────────────────────────────────────────────────

function SubCard({ sub, onEdit, onDelete, onSelect, onOpenActions }) {
  const cat   = CATEGORY_META[sub.category] || CATEGORY_META.other;
  const days  = daysUntil(sub.next_renewal_date);
  const rl    = renewalLabel(days);
  const sc    = STATUS_CONFIG[sub.status] || STATUS_CONFIG.active;
  const lowUse = isLowUseSubscription(sub);
  const edgeClass = subscriptionEdgeClass(sub, days, lowUse);

  return (
    <div className={`hm-sub-card ${edgeClass}${sub.status === "cancelled" ? " is-cancelled" : ""}`}
      onClick={() => onSelect(sub)} style={{ cursor: "pointer" }}>
      <div className="hm-sub-card-head">
        <span className="hm-sub-card-emoji">{sub.logo_emoji || cat.emoji}</span>
        <div className="hm-sub-card-info">
          <div className="hm-sub-card-name">{sub.name}</div>
          <div className="hm-sub-card-meta">
            {cat.label}
            {sub.is_shared && <span className="hm-sub-shared-badge">👥 {sharedSplitText(sub)}</span>}
          </div>
        </div>
        <div className="hm-sub-card-right">
          <div className="hm-sub-card-amount">{fmt$(sub.monthly_amount)}<span>/mo</span></div>
          {sub.billing_cycle !== "monthly" && (
            <div className="hm-sub-card-cycle">{sub.billing_cycle}</div>
          )}
        </div>
      </div>
      <div className="hm-sub-card-foot">
        <span className={lowUse ? "hm-sub-status-lowuse" : sc.cls}>{lowUse ? "Low use" : sc.label}</span>
        {rl && <span className={rl.cls}>🔔 {rl.text}</span>}
        {sub.usage_rating && <span className="hm-sub-rating">{"⭐".repeat(sub.usage_rating)}</span>}
        <div className="hm-sub-card-actions">
          <button type="button" className="hm-sub-card-btn"
            onClick={e => { e.stopPropagation(); onEdit(sub); }}>Edit</button>
          <button type="button" className="hm-sub-card-btn hm-sub-card-btn-danger"
            onClick={e => { e.stopPropagation(); onDelete(sub); }}>Remove</button>
        </div>
        <button type="button" className="hm-sub-card-more"
          onClick={e => { e.stopPropagation(); onOpenActions(sub); }}
          aria-label={`More actions for ${sub.name}`}>···</button>
      </div>
    </div>
  );
}

function SavedReportRow({ label, report, expanded, onToggle, children }) {
  if (!report) return null;
  const date = report.created_at ? fmtDate(report.created_at.slice(0, 10)) : "Saved";
  return (
    <div className={`hm-sub-saved-report${report.needs_refresh ? " needs-refresh" : ""}`}>
      <button type="button" className="hm-sub-saved-report-toggle" onClick={onToggle} aria-expanded={expanded}>
        <span>{report.needs_refresh ? "Needs refresh" : `Generated ${date}`}</span>
        <strong>{label}</strong>
        {report.stale_reason && <small>{report.stale_reason}</small>}
        <em aria-hidden="true">{expanded ? "⌃" : "⌄"}</em>
      </button>
      {expanded && (
        <div className="hm-sub-saved-report-drawer">
          {children}
        </div>
      )}
    </div>
  );
}

function ReplaceReportBanner({ onCancel, onReplace }) {
  return (
    <div className="hm-sub-replace-modal-shell" role="dialog" aria-modal="true">
      <button type="button" className="hm-sub-replace-modal-scrim" onClick={onCancel} aria-label="Cancel replacement" />
      <div className="hm-sub-replace-modal">
        <SheetLogoRow title="Replace report" onClose={onCancel} closeLabel="Cancel replacement" />
        <div className="hm-sub-replace-modal-body">
          <strong>Generate a new report?</strong>
          <p>The current saved report will be replaced. There is no report history.</p>
          <div className="hm-sub-replace-modal-actions">
            <button type="button" onClick={onCancel}>Cancel</button>
            <button type="button" onClick={onReplace}>Replace report</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Subscription detail / AI panel ───────────────────────────────────────────

function SubDetail({ sub, onBack, onEdit, onUpdated }) {
  const { applyAiUsage } = useAuth();
  const [keepResult,   setKeepResult]   = useState(null);
  const [scriptResult, setScriptResult] = useState(null);
  const [loadingKeep,  setLoadingKeep]  = useState(false);
  const [loadingScript,setLoadingScript]= useState(false);
  const [scriptAction, setScriptAction] = useState("cancel");
  const [scriptReason, setScriptReason] = useState("");
  const [aiError,      setAiError]      = useState("");
  const [usageLogs,    setUsageLogs]    = useState([]);
  const [priceHistory, setPriceHistory] = useState([]);
  const [loggingUse,   setLoggingUse]   = useState(false);
  const [savedReports, setSavedReports] = useState({});
  const [replacePrompt, setReplacePrompt] = useState("");
  const [expandedReport, setExpandedReport] = useState("");
  const [renewalBusy, setRenewalBusy] = useState(false);
  const showAiNotice = useMinLoad(loadingKeep || loadingScript, 700);

  useEffect(() => {
    subscriptionsApi.getUsage(sub.id).then(setUsageLogs).catch(() => {});
    subscriptionsApi.getPriceHistory(sub.id).then(setPriceHistory).catch(() => {});
  }, [sub.id]);

  useEffect(() => {
    let cancelled = false;
    async function loadReports() {
      const entries = await Promise.allSettled([
        subscriptionsApi.getReport(sub.id, "audit"),
        subscriptionsApi.getReport(sub.id, "cancel_script"),
        subscriptionsApi.getReport(sub.id, "negotiate_script"),
      ]);
      if (cancelled) return;
      const next = {};
      [["audit", entries[0]], ["cancel_script", entries[1]], ["negotiate_script", entries[2]]].forEach(([key, res]) => {
        if (res.status === "fulfilled" && !res.value?.error) next[key] = res.value;
      });
      setSavedReports(next);
      if (next.audit?.result_json) setKeepResult(next.audit.result_json);
    }
    loadReports();
    return () => { cancelled = true; };
  }, [sub.id]);

  useEffect(() => {
    const currentScript = savedReports[scriptAction === "negotiate" ? "negotiate_script" : "cancel_script"];
    if (currentScript?.result_json) setScriptResult({ action: scriptAction, ...currentScript.result_json });
    else setScriptResult(null);
    setReplacePrompt("");
  }, [scriptAction, savedReports]);

  async function logUseToday() {
    setLoggingUse(true);
    try {
      await subscriptionsApi.logUsage(sub.id, { used_today: true });
      const logs = await subscriptionsApi.getUsage(sub.id);
      setUsageLogs(logs);
    } catch {}
    finally { setLoggingUse(false); }
  }

  async function markRenewed() {
    const nextDate = advanceRenewalDate(sub.next_renewal_date, sub.billing_cycle, true);
    if (!nextDate) return;
    setRenewalBusy(true);
    setAiError("");
    try {
      const updated = await subscriptionsApi.update(sub.id, { next_renewal_date: nextDate });
      onUpdated?.(updated);
    } catch (ex) {
      setAiError(ex.message || "Could not update renewal date.");
    } finally {
      setRenewalBusy(false);
    }
  }

  const usedDaysCount = usageLogs.filter(l => l.used_today).length;
  const usageWindow = usageWindowFor(sub, usageLogs);
  const todayStr = new Date().toISOString().split("T")[0];
  const usedToday = usageLogs.some(l => l.log_date === todayStr && l.used_today);

  async function runKeepScore(force = false) {
    if (!force && savedReports.audit && !savedReports.audit.needs_refresh) {
      setReplacePrompt("audit");
      return;
    }
    setLoadingKeep(true); setAiError("");
    try {
      const r = await subscriptionsApi.generateReport(sub.id, "audit", { force });
      if (r.needs_confirm) { setReplacePrompt("audit"); return; }
      if (r.error === "ai_not_configured") { setAiError("AI not configured — add an API key in settings."); return; }
      if (r.error === "ai_limit_reached") { setAiError(r.message || "AI request limit reached."); return; }
      applyAiUsage?.(r.ai_usage);
      setKeepResult(r);
      setSavedReports(prev => ({ ...prev, audit: { result_json: r, needs_refresh: false, created_at: new Date().toISOString() } }));
      setExpandedReport("audit");
      setReplacePrompt("");
    } catch (ex) { setAiError(ex.message); }
    finally { setLoadingKeep(false); }
  }

  async function runScript(actionOverride, force = false) {
    const actionToRun = actionOverride || scriptAction;
    if (actionOverride) setScriptAction(actionOverride);
    const reportType = actionToRun === "negotiate" ? "negotiate_script" : "cancel_script";
    if (!force && savedReports[reportType] && !savedReports[reportType].needs_refresh) {
      setReplacePrompt(reportType);
      return;
    }
    setLoadingScript(true); setAiError("");
    try {
      const r = await subscriptionsApi.generateReport(sub.id, reportType, { force, reason: scriptReason });
      if (r.needs_confirm) { setReplacePrompt(reportType); return; }
      if (r.error === "ai_not_configured") { setAiError("AI not configured — add an API key in settings."); return; }
      if (r.error === "ai_limit_reached") { setAiError(r.message || "AI request limit reached."); return; }
      applyAiUsage?.(r.ai_usage);
      setScriptResult({ action: actionToRun, ...r });
      setSavedReports(prev => ({ ...prev, [reportType]: { result_json: { action: actionToRun, ...r }, needs_refresh: false, created_at: new Date().toISOString() } }));
      setExpandedReport(reportType);
      setReplacePrompt("");
    } catch (ex) { setAiError(ex.message); }
    finally { setLoadingScript(false); }
  }

  const cat = CATEGORY_META[sub.category] || CATEGORY_META.other;
  const sc  = STATUS_CONFIG[sub.status]   || STATUS_CONFIG.active;
  const monthly = Number(sub.monthly_amount || 0);
  const annualCost = monthly * 12;
  const renewalDays = daysUntil(sub.next_renewal_date);
  const renewal = renewalLabel(renewalDays);
  const renewalDateIsStale = sub.status === "active" && renewalDays != null && renewalDays < 0;
  const renewalNeedsAction = sub.status === "active" && renewalDays != null && renewalDays <= 7;
  const advancedRenewalDate = renewalNeedsAction ? advanceRenewalDate(sub.next_renewal_date, sub.billing_cycle, true) : "";
  const usageRating = getUsageRating(sub);
  const ratingText = usageRating ? `${usageRating}/5` : "Not rated";
  const lastUsedLog = usageLogs.find(l => l.used_today);
  const latestPrice = priceHistory[0];
  const scriptUrl = scriptCancelUrl(sub, scriptResult);
  const keepVerdictTone = keepResult?.verdict_color || (
    ["Keep"].includes(keepResult?.verdict) ? "green" :
    ["Cancel", "Pause"].includes(keepResult?.verdict) ? "red" :
    keepResult?.verdict ? "amber" : "amber"
  );
  const householdSignals = (keepResult?.cross_module_findings || keepResult?.evidence || [])
    .filter(Boolean)
    .slice(0, 3);
  const primarySignal = householdSignals.find(item => typeof item !== "string") || null;
  const suggestedReason = scriptReasonFromFinding(primarySignal, sub.name);
  const quickFacts = [
    sub.next_renewal_date && ["Renews", fmtDate(sub.next_renewal_date)],
    ["Annual cost", fmt$(annualCost)],
    sub.billing_cycle && ["Cycle", sub.billing_cycle],
    sub.payment_method && ["Payment", sub.payment_method],
    sub.start_date && ["Started", fmtDate(sub.start_date)],
    sub.is_shared && ["Shared", sharedSplitText(sub)],
  ].filter(Boolean);
  const scriptLines = scriptResult?.script ? [
    ["Opening", scriptResult.script.opening, "start"],
    ["If they offer a deal", scriptResult.script.if_they_offer_deal, "counter"],
    ["If they push back", scriptResult.script.if_they_push_back, "counter"],
    ["Close", scriptResult.script.closing, "close"],
  ].filter(([, line]) => line) : [];

  return (
    <div className="hm-sub-detail">
      {showAiNotice && (
        <SavingNotice
          title={loadingKeep ? "Analyzing" : "Writing"}
          message={loadingKeep ? "Checking subscription value..." : "Preparing your script..."}
        />
      )}
      <div className="hm-sub-detail-head">
        <button type="button" className="hm-sub-detail-back" onClick={onBack}>← Subscriptions</button>
        <div className="hm-sub-detail-title-row">
          <span className="hm-sub-detail-logo">{sub.logo_emoji || cat.emoji}</span>
          <div>
            <span>{cat.label}</span>
            <h1>{sub.name}</h1>
          </div>
          <strong>{fmt$(monthly)}<small>/mo</small></strong>
        </div>
        <div className="hm-sub-detail-status-row">
          <span className={sc.cls}>{sc.label}</span>
          {renewal && (
            <span className={renewal.cls}>
              {renewalDateIsStale ? "Update renewal date" : `Renews ${renewal.text.replace("Renewal ", "")}`}
            </span>
          )}
          <span>{ratingText} value</span>
        </div>
        {renewalNeedsAction && (
          <div className="hm-sub-renewal-maintenance hm-sub-renewal-maintenance--detail">
            <span>
              {renewalDateIsStale
                ? "Renewal date passed. If it was paid, update the saved next-renewal date."
                : "If this renewal has been paid or handled, update the saved next-renewal date."}
            </span>
            <button type="button" onClick={markRenewed} disabled={renewalBusy}>
              {renewalBusy ? "Updating..." : `Mark renewed → ${fmtDate(advancedRenewalDate)}`}
            </button>
          </div>
        )}
      </div>

      <div className="hm-sub-detail-flow">
        <section className="hm-sub-detail-facts" aria-label="Subscription facts">
          {quickFacts.map(([label, value]) => (
            <div key={label}>
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </section>
      </div>

      {/* Usage tracker */}
      <section className="hm-sub-flow-section hm-sub-usage-flow">
        <div className="hm-sub-flow-head">
          <div>
            <span>Usage rhythm</span>
            <strong>{usedDaysCount > 0 ? usageWindow.helper : "No usage logged yet"}</strong>
          </div>
          <button type="button" className="hm-vault-manual-btn" onClick={logUseToday}
            disabled={loggingUse || usedToday}>
            {loggingUse ? "Logging…" : usedToday ? "✓ Logged today" : "Log today's use"}
          </button>
        </div>
        {usageLogs.length > 0 ? (
          <div className="hm-sub-usage-dots">
            {usageLogs.slice(0, 30).map(l => (
              <span key={l.log_date}
                className={`hm-sub-usage-dot${l.used_today ? " is-used" : ""}`}
                title={l.log_date} />
            ))}
          </div>
        ) : (
          <p className="hm-sub-usage-empty">No usage logged yet. Tap the button each day you use this subscription.</p>
        )}
        <div className="hm-sub-usage-readout">
          <div><span>{usageWindow.label}</span><strong>{usageLogs.length > 0 ? `${usageWindow.count}x` : "Learning"}</strong></div>
          <div><span>Last marked</span><strong>{lastUsedLog ? fmtDate(lastUsedLog.log_date) : "Never"}</strong></div>
          <div><span>Value</span><strong>{ratingText}</strong></div>
        </div>
      </section>

      {/* Price history */}
      {priceHistory.length > 0 && (
        <section className="hm-sub-flow-section hm-sub-price-flow">
          <div className="hm-sub-flow-head">
            <div>
              <span>Price history</span>
              <strong>{latestPrice ? `${latestPrice.change_type === "increase" ? "Increase" : "Decrease"} logged` : "Tracked changes"}</strong>
            </div>
          </div>
          <div className="hm-sub-price-history">
            {priceHistory.map((h, i) => (
              <div key={i} className="hm-sub-price-row">
                <span className="hm-sub-price-date">{fmtDate(h.change_date)}</span>
                <span className={`hm-sub-price-change hm-sub-price-${h.change_type}`}>
                  {h.change_type === "increase" ? "▲" : "▼"} {fmt$(h.previous_amount ?? h.old_amount)} → {fmt$(h.new_amount)}
                </span>
                {h.notes && <span className="hm-sub-price-note">{h.notes}</span>}
              </div>
            ))}
          </div>
        </section>
      )}

      {aiError && <div className="hm-inline-error" style={{ margin: "12px 0" }}>{aiError}</div>}

      {/* Should I keep this? */}
      <section className="hm-sub-flow-section hm-sub-score-flow hm-sub-ai-primary">
        <div className="hm-sub-flow-head">
          <div>
            <span>Keep audit</span>
            <strong>Why am I still paying for this?</strong>
          </div>
          <button type="button" className="hm-vault-manual-btn" onClick={() => runKeepScore()} disabled={loadingKeep}>
            {loadingKeep ? "Auditing…" : savedReports.audit ? (savedReports.audit.needs_refresh ? "Refresh audit" : "Re-run audit") : "Audit"}
          </button>
        </div>
        <SavedReportRow
          label="Active audit"
          report={savedReports.audit}
          expanded={expandedReport === "audit"}
          onToggle={() => setExpandedReport(expandedReport === "audit" ? "" : "audit")}
        >
          {keepResult && !loadingKeep && (
            <div className="hm-sub-keep-result">
              <div className={`hm-sub-verdict hm-sub-verdict-${keepVerdictTone}`}>
                <span className="hm-sub-verdict-label">{keepResult.verdict}</span>
                <span className="hm-sub-verdict-score">{keepResult.score ? `${keepResult.score}/10` : keepResult.confidence || "audit"}</span>
              </div>
              <p className="hm-sub-reasoning">{keepResult.reasoning}</p>
              {keepResult.primary_action && (
                <div className="hm-sub-alternative">{keepResult.primary_action}</div>
              )}
              {keepResult.action_steps?.length > 0 && (
                <ul className="hm-sub-steps">
                  {keepResult.action_steps.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              )}
              {!keepResult.action_steps?.length && keepResult.evidence?.length > 0 && (
                <ul className="hm-sub-steps">
                  {keepResult.evidence.slice(0, 3).map((item, i) => (
                    <li key={i}>{typeof item === "string" ? item : item.message || item.title}</li>
                  ))}
                </ul>
              )}
              {keepResult.cheaper_alternative && (
                <div className="hm-sub-alternative">💡 Lower-cost option: {keepResult.cheaper_alternative}</div>
              )}
              {householdSignals.length > 0 && (
                <div className="hm-sub-household-signals">
                  <div className="hm-sub-household-signals-head">
                    <span>Household signals</span>
                    {suggestedReason && (
                      <button type="button" onClick={() => setScriptReason(suggestedReason)}>Use for script</button>
                    )}
                  </div>
                  {householdSignals.map((finding, i) => {
                    const isText = typeof finding === "string";
                    return (
                      <div key={i} className="hm-sub-signal-row">
                        <strong>{isText ? "Household" : signalSourceLabel(finding)}</strong>
                        <span>{isText ? finding : signalReason(finding)}</span>
                        {!isText && finding.estimated_monthly_savings ? <em>{fmt$(finding.estimated_monthly_savings)}/mo</em> : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </SavedReportRow>
        {replacePrompt === "audit" && (
          <ReplaceReportBanner onCancel={() => setReplacePrompt("")} onReplace={() => runKeepScore(true)} />
        )}
        {!keepResult && !loadingKeep && !savedReports.audit && (
          <div className="hm-sub-ai-empty">
            <p>HouseMind weighs price, usage, renewal timing, and household context from utilities, vehicles, gifts, food, and Home Vault.</p>
            <div className="hm-sub-ai-empty-grid">
              <span><strong>{fmt$(annualCost)}</strong><small>Annual cost</small></span>
              <span><strong>{usedDaysCount || "No"}</strong><small>Usage logs</small></span>
              <span><strong>{renewal ? renewal.text : "Missing"}</strong><small>Renewal signal</small></span>
            </div>
          </div>
        )}
      </section>

      {/* Cancel / negotiate script */}
      <section className="hm-sub-flow-section hm-sub-script-flow hm-sub-ai-report-flow">
        <div className="hm-sub-flow-head">
          <div>
            <span>AI report</span>
            <strong>Turn the score into a cancellation or negotiation plan</strong>
          </div>
        </div>
        {!scriptResult && !loadingScript && (
          <div className="hm-sub-ai-report-intro">
            <div>
              <span>What you get</span>
              <strong>Channel, best time, likely outcome, exact words, and pushback counters.</strong>
            </div>
            <div>
              <span>Best after scoring</span>
              <strong>Use Keep score first when you want the recommendation and script to agree.</strong>
            </div>
          </div>
        )}
        {primarySignal && !scriptResult && (
          <div className="hm-sub-script-case-file">
            <span>{signalSourceLabel(primarySignal)} case file</span>
            <strong>{primarySignal.title || "Household signal"}</strong>
            <p>{signalReason(primarySignal)}</p>
            {primarySignal.evidence?.length > 0 && <small>{primarySignal.evidence.slice(0, 2).join(" · ")}</small>}
          </div>
        )}
        {suggestedReason && !scriptReason && !scriptResult && (
          <button type="button" className="hm-sub-script-reason-suggest" onClick={() => setScriptReason(suggestedReason)}>
            Use household signal as reason
          </button>
        )}
        <SavedReportRow
          label={scriptAction === "negotiate" ? "Active negotiation script" : "Active cancellation script"}
          report={savedReports[scriptAction === "negotiate" ? "negotiate_script" : "cancel_script"]}
          expanded={expandedReport === (scriptAction === "negotiate" ? "negotiate_script" : "cancel_script")}
          onToggle={() => {
            const key = scriptAction === "negotiate" ? "negotiate_script" : "cancel_script";
            setExpandedReport(expandedReport === key ? "" : key);
          }}
        >
          {scriptResult && !loadingScript && (
            <div className="hm-sub-script-result hm-sub-script-playbook">
              <div className="hm-sub-script-card-head">
                <div>
                  <span>{scriptResult.action === "negotiate" ? "Negotiation playbook" : "Cancellation playbook"}</span>
                  <strong>{sub.name}</strong>
                </div>
                <em>{scriptResult.channel_recommendation || "Best channel"}</em>
              </div>
              <div className="hm-sub-script-tip">
                <strong>{scriptResult.channel_recommendation || "Recommended channel"}</strong>
                <span>{scriptResult.channel_reasoning || "Use the channel where you can save a confirmation record."}</span>
                {scriptUrl && (
                  <a href={scriptUrl} target="_blank" rel="noreferrer">Open provider →</a>
                )}
              </div>
              <div className="hm-sub-script-intel">
                <div><span>Best time</span><strong>{scriptResult.best_time || "Anytime"}</strong></div>
                <div><span>Realistic result</span><strong>{scriptResult.realistic_outcome || "Clear next step"}</strong></div>
              </div>
              {scriptLines.length > 0 && (
                <div className="hm-sub-script-lines">
                  {scriptLines.map(([label, line, kind]) => (
                    <div key={label} className={`hm-sub-script-line hm-sub-script-line--${kind}`}>
                      <div className="hm-sub-script-line-head">
                        <strong>{label}</strong>
                        <button type="button" onClick={() => copyToClipboard(line)}>Copy</button>
                      </div>
                      <p>{line}</p>
                      {kind === "start" && <small>Use this as your opener. Keep it short and concrete.</small>}
                      {kind === "counter" && <small>Use this only if the provider creates friction or offers a save deal.</small>}
                      {kind === "close" && <small>Ask for confirmation and save the date.</small>}
                    </div>
                  ))}
                </div>
              )}
              {scriptResult.magic_words?.length > 0 && (
                <div className="hm-sub-magic-words">
                  <strong>Say these</strong>
                  {scriptResult.magic_words.map((w, i) => (
                    <button key={i} type="button" className="hm-sub-magic-word" onClick={() => copyToClipboard(w)}>{w}</button>
                  ))}
                </div>
              )}
              {scriptResult.watch_out_for && (
                <div className="hm-sub-watch-out"><strong>Watch out</strong><span>{scriptResult.watch_out_for}</span></div>
              )}
              <div className="hm-sub-script-actions">
                <button type="button" onClick={() => runScript(scriptResult.action === "cancel" ? "negotiate" : "cancel")} disabled={loadingScript}>
                  {scriptResult.action === "cancel" ? "Negotiate instead" : "Cancel instead"}
                </button>
                {scriptUrl && (
                  <a href={scriptUrl} target="_blank" rel="noreferrer">
                    {scriptResult.action === "cancel" ? "Cancel now" : "Open provider"}
                  </a>
                )}
              </div>
            </div>
          )}
        </SavedReportRow>
        {replacePrompt === (scriptAction === "negotiate" ? "negotiate_script" : "cancel_script") && (
          <ReplaceReportBanner onCancel={() => setReplacePrompt("")} onReplace={() => runScript(scriptAction, true)} />
        )}
        <div className="hm-sub-script-controls">
          <select className="hm-auth-input hm-sub-ai-action-select" value={scriptAction} onChange={e => setScriptAction(e.target.value)} style={{ flex: 1 }}>
            <option value="cancel">Cancel subscription</option>
            <option value="negotiate">Negotiate lower price</option>
          </select>
          <input className="hm-auth-input" placeholder="Your reason (optional)…"
            value={scriptReason} onChange={e => setScriptReason(e.target.value)} style={{ flex: 2 }} />
          <button type="button" className="hm-vault-manual-btn" onClick={() => runScript()} disabled={loadingScript}>
            {loadingScript ? "Writing…" : savedReports[scriptAction === "negotiate" ? "negotiate_script" : "cancel_script"] ? "Re-run script" : "Generate script"}
          </button>
        </div>
      </section>

      {onEdit && (
        <div className="hm-util-inline-edit-row">
          <button type="button" className="hm-util-inline-edit-btn" onClick={onEdit}>✏️ Edit subscription</button>
        </div>
      )}
    </div>
  );
}

// ── Waste report panel ────────────────────────────────────────────────────────

function WasteReport({ onClose }) {
  const { applyAiUsage } = useAuth();
  const [report,  setReport]  = useState(null);
  const [savedReport, setSavedReport] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [replacePrompt, setReplacePrompt] = useState(false);
  const showLoading = useMinLoad(loading, 700);

  useEffect(() => {
    subscriptionsApi.savedWasteReport()
      .then(r => {
        setSavedReport(r);
        setReport(r.result_json);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [applyAiUsage]);

  async function generate(force = false) {
    if (savedReport && !savedReport.needs_refresh && !force) {
      setReplacePrompt(true);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const r = await subscriptionsApi.wasteReport({ force });
      if (r.needs_confirm) { setReplacePrompt(true); return; }
      if (r.error === "ai_not_configured") { setError("AI not configured — add an API key in settings."); return; }
      if (r.error === "ai_limit_reached") { setError(r.message || "AI request limit reached."); return; }
      applyAiUsage?.(r.ai_usage);
      setReport(r);
      setSavedReport({ result_json: r, needs_refresh: false, created_at: new Date().toISOString() });
      setReportOpen(true);
      setReplacePrompt(false);
    } catch (ex) { setError(ex.message); }
    finally { setLoading(false); }
  }

  const scoreColor = !report ? "var(--hm-muted)" :
    report.health_score >= 80 ? "var(--hm-mint)" :
    report.health_score >= 50 ? "var(--hm-amber)" : "var(--hm-red)";
  const healthScore = report?.health_score ?? "—";
  const healthLabel = report?.health_label || "Review";
  const householdSignals = (report?.cross_module_findings || []).slice(0, 4);

  return (
    <div className="hm-panel hm-sub-report-panel">
      {showLoading && <SavingNotice title="Analyzing" message="Finding subscription waste..." />}
      <div className="hm-panel-head">
        <div><p>Subscription review</p><h2>Monthly waste report</h2></div>
        <div className="hm-sub-report-head-actions">
          {savedReport && (
            <button type="button" className="hm-vault-manual-btn" onClick={() => savedReport.needs_refresh ? generate(true) : setReplacePrompt(true)} disabled={loading}>
              {savedReport.needs_refresh ? "Refresh" : "Re-run"}
            </button>
          )}
          <button type="button" className="hm-vault-cancel-btn" onClick={onClose}>✕</button>
        </div>
      </div>
      {loading && <div className="hm-sub-loading">Analyzing subscriptions...</div>}
      {error   && <div className="hm-inline-error">{error}</div>}
      {savedReport && (
        <SavedReportRow
          label="Active waste report"
          report={savedReport}
          expanded={reportOpen}
          onToggle={() => setReportOpen(open => !open)}
        >
          {report && (
            <div className="hm-sub-report-body">
              <div className="hm-sub-report-scores">
                <div className="hm-sub-report-score-card">
                  <span className="hm-sub-score-label">Total monthly</span>
                  <strong style={{ color: "var(--hm-cyan)" }}>{fmt$(report.total_monthly)}</strong>
                </div>
                <div className="hm-sub-report-score-card">
                  <span className="hm-sub-score-label">Estimated waste</span>
                  <strong style={{ color: "var(--hm-red)" }}>{fmt$(report.estimated_waste)}</strong>
                </div>
                <div className="hm-sub-report-score-card">
                  <span className="hm-sub-score-label">Health score</span>
                  <strong style={{ color: scoreColor }}>{healthScore}/100 · {healthLabel}</strong>
                </div>
              </div>
              <p className="hm-sub-report-summary">{report.summary}</p>

              {householdSignals.length > 0 && (
                <div className="hm-sub-report-section hm-sub-report-signals">
                  <h3>Household signals</h3>
                  {householdSignals.map((finding, i) => (
                    <div key={i} className="hm-sub-report-signal">
                      <div>
                        <span>{signalSourceLabel(finding)}</span>
                        <strong>{finding.title || "Household context"}</strong>
                        <p>{signalReason(finding)}</p>
                        {finding.evidence?.length > 0 && (
                          <small>{finding.evidence.slice(0, 2).join(" · ")}</small>
                        )}
                      </div>
                      {finding.estimated_monthly_savings ? (
                        <em>{fmt$(finding.estimated_monthly_savings)}/mo</em>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}

              {report.cancel_recommendations?.length > 0 && (
                <div className="hm-sub-report-section">
                  <h3>🗑 Consider cancelling</h3>
                  {report.cancel_recommendations.map((r, i) => (
                    <div key={i} className="hm-sub-report-item">
                      <div className="hm-sub-report-item-name">{r.name} <span className="hm-sub-savings">saves {fmt$(r.monthly_savings)}/mo</span></div>
                      <p>{r.reason}</p>
                    </div>
                  ))}
                </div>
              )}
              {report.downgrade_recommendations?.length > 0 && (
                <div className="hm-sub-report-section">
                  <h3>⬇️ Consider downgrading</h3>
                  {report.downgrade_recommendations.map((r, i) => (
                    <div key={i} className="hm-sub-report-item">
                      <div className="hm-sub-report-item-name">{r.name} <span className="hm-sub-savings">saves {fmt$(r.monthly_savings)}/mo</span></div>
                      <p>{r.current_plan} → {r.suggested_plan}</p>
                    </div>
                  ))}
                </div>
              )}
              {report.duplicate_alerts?.length > 0 && (
                <div className="hm-sub-report-section">
                  <h3>⚠️ Possible duplicates</h3>
                  {report.duplicate_alerts.map((r, i) => (
                    <div key={i} className="hm-sub-report-item">
                      <div className="hm-sub-report-item-name">{r.names.join(" + ")} <span className="hm-sub-savings">saves {fmt$(r.monthly_savings)}/mo</span></div>
                      <p>{r.overlap_description}</p>
                    </div>
                  ))}
                </div>
              )}
              {report.cheaper_alternatives?.length > 0 && (
                <div className="hm-sub-report-section">
                  <h3>💡 Cheaper alternatives</h3>
                  {report.cheaper_alternatives.map((r, i) => (
                    <div key={i} className="hm-sub-report-item">
                      <div className="hm-sub-report-item-name">{r.current_name} → {r.alternative_name} <span className="hm-sub-savings">saves {fmt$(r.monthly_savings)}/mo</span></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </SavedReportRow>
      )}
      {replacePrompt && (
        <ReplaceReportBanner onCancel={() => setReplacePrompt(false)} onReplace={() => generate(true)} />
      )}
      {!loading && !report && !error && (
        <button type="button" className="hm-veh-add-btn" onClick={() => generate(true)}>Generate waste report</button>
      )}
    </div>
  );
}

function UnifiedMoneyPanel({ summary, busy, notice, onImport, subsMonthly }) {
  const fileRef = useRef(null);
  const obligations = summary?.obligations || [];
  const extras = obligations.filter(o => o.type !== "subscription").slice(0, 4);
  const anomalies = summary?.active_anomalies || 0;
  return (
    <section className="hm-money-panel">
      <div className="hm-money-panel__totals">
        <div><strong>{fmt$(summary?.monthly_total || 0)}</strong><span>/mo household total</span></div>
        {subsMonthly != null && (
          <small>{fmt$(subsMonthly)} of that is subscriptions below, rest is utilities &amp; detected charges</small>
        )}
      </div>
      <div className="hm-money-panel__stats-row">
        <span><b>{obligations.length}</b> obligations</span>
        <span><b>{summary?.transaction_count || 0}</b> transactions analyzed</span>
        <span className={anomalies ? "is-alert" : ""}><b>{anomalies}</b> open anomal{anomalies === 1 ? "y" : "ies"}</span>
      </div>
      {extras.length > 0 && (
        <div className="hm-money-panel__extras">
          <span>Beyond subscriptions</span>
          {extras.map((item, index) => (
            <div key={`${item.type}-${item.name}-${index}`}>
              <em>{item.type.replaceAll("_", " ")}</em>
              <strong>{item.name}</strong>
              <b>{item.current != null ? fmt$(item.current) : "Pending"}</b>
            </div>
          ))}
        </div>
      )}
      <div className="hm-money-panel__import">
        <span>{notice || "Upload a bank CSV export to catch recurring charges HouseMind hasn't matched yet."}</span>
        <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={event => { const file = event.target.files?.[0]; if (file) onImport(file); event.target.value = ""; }} />
        <button type="button" disabled={busy} onClick={() => fileRef.current?.click()}>{busy ? "Analyzing…" : "Upload CSV"}</button>
      </div>
    </section>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SubscriptionsPage() {
  const [subs,        setSubs]        = useState([]);
  const [totalMonthly,setTotalMonthly]= useState(0);
  const [loading,     setLoading]     = useState(true);
  const [view,        setView]        = useState("list");   // list | quickadd | addform | editform | detail | report
  const [selected,    setSelected]    = useState(null);
  const [editTarget,  setEditTarget]  = useState(null);
  const [filterStatus,setFilterStatus]= useState("active");
  const [filterCat,   setFilterCat]   = useState("all");
  const [error,       setError]       = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [undoDelete, setUndoDelete] = useState(null); // { sub, timerId }
  const [deletingId, setDeletingId] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [signals, setSignals] = useState([]);
  const [actionSub, setActionSub] = useState(null);
  const [insightSub, setInsightSub] = useState(null);
  const [moneySummary, setMoneySummary] = useState(null);
  const [moneyBusy, setMoneyBusy] = useState(false);
  const [moneyNotice, setMoneyNotice] = useState("");
  const presentation = useFormPresentation();
  const showDeleting = useMinLoad(!!deletingId, 700);
  useBodyScrollLock(Boolean(actionSub || insightSub));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      await subscriptionsApi.runDailyMaintenance().catch(() => null);
      const data = await subscriptionsApi.list();
      setSubs(data.subscriptions || []);
      setTotalMonthly(data.total_monthly || 0);
      subscriptionsApi.getAlerts().then(setAlerts).catch(() => {});
      subscriptionsApi.signals().then(r => setSignals(r?.signals || [])).catch(() => {});
      financialApi.summary().then(setMoneySummary).catch(() => {});
    } catch (ex) {
      setError(ex.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function importStatement(file) {
    setMoneyBusy(true); setMoneyNotice("");
    try {
      const result = await financialApi.importCsv(file, file.name);
      setMoneySummary(result.summary);
      setMoneyNotice(`${result.created} transaction${result.created === 1 ? "" : "s"} added; ${result.cases_created} new Case${result.cases_created === 1 ? "" : "s"} opened.`);
    } catch (ex) { setMoneyNotice(ex.message || "Statement import failed."); }
    finally { setMoneyBusy(false); }
  }

  function handleSaved(sub) {
    setSubs(prev => {
      const idx = prev.findIndex(s => s.id === sub.id);
      if (idx >= 0) { const n = [...prev]; n[idx] = sub; return n; }
      return [sub, ...prev];
    });
    setTotalMonthly(prev => {
      const ma = sub.monthly_amount || 0;
      return Math.round((prev + (sub.status === "active" ? ma : 0)) * 100) / 100;
    });
    setView("list");
    setEditTarget(null);
    load(); // refresh for accurate total
  }

  function commitRemoveSubscription(sub) {
    setDeletingId(sub.id);
    subscriptionsApi.remove(sub.id)
      .then(() => load())
      .catch(ex => {
        setSubs(prev => prev.find(s => s.id === sub.id) ? prev : [sub, ...prev]);
        setError(ex.message || "Could not remove this subscription.");
      })
      .finally(() => setDeletingId(null));
  }

  function removeSubscriptionWithUndo(sub) {
    if (undoDelete?.timerId) {
      clearTimeout(undoDelete.timerId);
      commitRemoveSubscription(undoDelete.sub);
    }
    setSubs(prev => prev.filter(s => s.id !== sub.id));
    setTotalMonthly(prev =>
      Math.round((prev - (sub.status === "active" ? (sub.monthly_amount || 0) : 0)) * 100) / 100
    );
    const timerId = setTimeout(() => {
      setUndoDelete(null);
      commitRemoveSubscription(sub);
    }, 6000);
    setUndoDelete({ sub, timerId });
  }

  function undoRemoveSubscription() {
    if (!undoDelete) return;
    clearTimeout(undoDelete.timerId);
    setSubs(prev => prev.find(s => s.id === undoDelete.sub.id) ? prev : [undoDelete.sub, ...prev]);
    setTotalMonthly(prev =>
      Math.round((prev + (undoDelete.sub.status === "active" ? (undoDelete.sub.monthly_amount || 0) : 0)) * 100) / 100
    );
    setUndoDelete(null);
  }

  async function toggleSubscriptionCancelled(sub, cancel) {
    setError("");
    try {
      const todayIso = new Date().toISOString().split("T")[0];
      const updated = await subscriptionsApi.update(sub.id, {
        status: cancel ? "cancelled" : "active",
        cancelled_date: cancel ? todayIso : null,
      });
      setSubs(prev => prev.map(s => s.id === sub.id ? updated : s));
      load();
    } catch (ex) {
      setError(ex.message || "Could not update subscription status.");
    }
  }

  function openInsightSheet(sub) {
    setActionSub(null);
    setInsightSub(sub);
  }

  function openActionSheet(sub) {
    setInsightSub(null);
    setActionSub(sub);
  }

  function closeSubscriptionSheets() {
    setActionSub(null);
    setInsightSub(null);
  }


  // Filter
  const filtered = subs.filter(s => {
    if (filterStatus !== "all" && s.status !== filterStatus) return false;
    if (filterCat    !== "all" && s.category !== filterCat)  return false;
    return true;
  });

  // Totals by category
  const catTotals = {};
  subs.filter(s => s.status === "active").forEach(s => {
    catTotals[s.category] = (catTotals[s.category] || 0) + (s.monthly_amount || 0);
  });
  const health = useMemo(() => summarizeSubscriptionHealth(subs, totalMonthly), [subs, totalMonthly]);
  const subById = useMemo(() => Object.fromEntries(subs.map(s => [s.id, s])), [subs]);

  // Renewal alerts (within 7 days)
  const renewingSoon = subs.filter(s => {
    if (s.status !== "active") return false;
    const d = daysUntil(s.next_renewal_date);
    return d != null && d >= 0 && d <= 7;
  });

  const actionItems = useMemo(() => {
    const items = [];
    health.trialsEnding.slice(0, 2).forEach(s => {
      const d = daysUntil(s.trial_end_date);
      items.push({
        key: `trial-${s.id}`,
        sub: s,
        tone: "urgent",
        kicker: "Trial",
        title: `${s.name} ends ${d === 0 ? "today" : `in ${d}d`}`,
        detail: "Review before it bills.",
      });
    });
    health.duplicates.slice(0, 2).forEach(d => {
      const first = d.items[0];
      items.push({
        key: `duplicate-${d.name}`,
        sub: first,
        tone: "save",
        kicker: "Duplicate",
        title: `${first.name} appears ${d.items.length} times`,
        detail: d.hint
          ? `${d.hint.label} may save ${fmtShort$(d.monthlySavings * 12)}/yr.`
          : "Review whether these can be consolidated.",
      });
    });
    health.annualSoon.slice(0, 2).forEach(s => {
      const d = daysUntil(s.next_renewal_date);
      items.push({
        key: `annual-${s.id}`,
        sub: s,
        tone: "warn",
        kicker: "Annual renewal",
        title: `${s.name} renews ${d === 0 ? "today" : `in ${d}d`}`,
        detail: `${fmt$(s.amount)} charge coming up.`,
      });
    });
    health.lowUse.slice(0, 2).forEach(s => {
      items.push({
        key: `low-use-${s.id}`,
        sub: s,
        tone: "save",
        kicker: "Possible waste",
        title: `${s.name} has low usage`,
        detail: `${fmt$(s.monthly_amount)}/mo. ${usageDetail(s)}`,
      });
    });
    health.crowdedCategories.slice(0, 1).forEach(c => {
      items.push({
        key: `category-${c.category}`,
        reportOnly: true,
        tone: "save",
        kicker: "Overlap check",
        title: `${c.count} ${CATEGORY_META[c.category]?.label || "similar"} subscriptions`,
        detail: "Run the waste report to compare them.",
      });
    });
    health.missingDates.slice(0, 1).forEach(s => {
      items.push({
        key: `missing-${s.id}`,
        sub: s,
        tone: "neutral",
        kicker: "Missing date",
        title: `${s.name} needs a renewal date`,
        detail: "Add it so HouseMind can alert you.",
      });
    });
    return items.slice(0, 4);
  }, [health]);

  if (view === "detail" && selected) {
    const detail = (
      <SubDetail
        sub={selected}
        onBack={() => { setSelected(null); setView("list"); }}
        onEdit={() => { setEditTarget(selected); setView("editform"); }}
        onUpdated={updated => {
          setSubs(prev => prev.map(s => s.id === updated.id ? updated : s));
          setSelected(updated);
          load();
        }}
      />
    );
    if (presentation === "modal") {
      return (
        <div className="hm-veh-page hm-sub-page">
          <CenteredModal title={selected.name || "Subscription"} onClose={() => { setSelected(null); setView("list"); }} width={720}>
            {detail}
            <SubscriptionsAskBar />
          </CenteredModal>
        </div>
      );
    }
    return (
      <div className="hm-veh-page hm-sub-page">
        {detail}
        <SubscriptionsAskBar />
      </div>
    );
  }

  if (view === "report") {
    return (
      <div className="hm-veh-page hm-sub-page">
        <WasteReport onClose={() => setView("list")} />
      </div>
    );
  }

  // ── List view ─────────────────────────────────────────────────────────────
  return (
    <div className="hm-veh-page hm-sub-page">
      {view === "quickadd" && (
        <QuickAddPanel
          onSaved={sub => { handleSaved(sub); setView("list"); }}
          onClose={() => setView("list")}
        />
      )}
      {(view === "addform" || view === "editform") && (
        <SubForm
          initial={editTarget}
          title={view === "editform" ? `Edit ${editTarget?.name}` : "Add subscription"}
          onSaved={handleSaved}
          onCancel={() => { setView("list"); setEditTarget(null); }}
        />
      )}
      <ConfirmRemoveSubscription
        sub={confirmDelete}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          const sub = confirmDelete;
          setConfirmDelete(null);
          removeSubscriptionWithUndo(sub);
        }}
      />
      {showDeleting && <SavingNotice title="Removing" message="Removing subscription..." />}
      {undoDelete && (
        <div className="hm-util-undo-toast" role="status" aria-live="polite">
          <span>{undoDelete.sub.name || "Subscription"} removed</span>
          <button type="button" onClick={undoRemoveSubscription}>Undo</button>
        </div>
      )}
      <SubscriptionMobileSheet
        sub={actionSub}
        onClose={closeSubscriptionSheets}
        onView={() => { setSelected(actionSub); closeSubscriptionSheets(); setView("detail"); }}
        onEdit={() => { setEditTarget(actionSub); closeSubscriptionSheets(); setView("editform"); }}
        onToggleCancel={(sub, cancel) => { toggleSubscriptionCancelled(sub, cancel); closeSubscriptionSheets(); }}
        onDelete={() => { setConfirmDelete(actionSub); closeSubscriptionSheets(); }}
      />
      <SubscriptionInsightSheet
        sub={insightSub}
        onClose={closeSubscriptionSheets}
        onView={() => { setSelected(insightSub); closeSubscriptionSheets(); setView("detail"); }}
        onUpdated={updated => {
          setSubs(prev => prev.map(s => s.id === updated.id ? updated : s));
          setInsightSub(updated);
          load();
        }}
      />
      <ModuleHeader
        className="hm-sub-module-header"
        title="Your subscriptions"
        subtitle="Catch renewals, find waste, and keep recurring costs intentional."
        image={IMG_SUBS}
      >
        {totalMonthly > 0 && (
          <div className="hm-module-stat">${totalMonthly.toFixed(2)}<span>/ mo</span></div>
        )}
        {subs.filter(s => s.status === "active").length > 0 && (
          <div className="hm-module-stat">
            {subs.filter(s => s.status === "active").length}<span>active</span>
          </div>
        )}
      </ModuleHeader>

      {error && <div className="hm-inline-error" style={{ marginBottom: 12 }}>{error}</div>}

      <MobileCollapseSection
        storageKey="hm_money_open"
        title="💰 Financial intelligence"
        badge={moneySummary?.obligations?.length ? `${moneySummary.obligations.length} tracked` : "Tap to view"}
      >
        <UnifiedMoneyPanel summary={moneySummary} busy={moneyBusy} notice={moneyNotice} onImport={importStatement} subsMonthly={totalMonthly} />
      </MobileCollapseSection>

      <div className="hm-sub-summary-bar">
        <div className="hm-sub-summary-card">
          <span>💳 Monthly total</span>
          <strong style={{ color: "var(--hm-cyan)" }}>{fmt$(totalMonthly)}</strong>
          <small>{subs.filter(s => s.status === "active").length} subscription{subs.filter(s => s.status === "active").length === 1 ? "" : "s"}</small>
        </div>
        <div className="hm-sub-summary-card">
          <span>📅 Annual exposure</span>
          <strong className="hm-sub-risk-value">{fmtShort$(health.annualExposure)}</strong>
          <small>if unchanged</small>
        </div>
        <div className="hm-sub-summary-card">
          <span>💚 Health score</span>
          <strong className="hm-sub-health-value">{health.healthScore}<small>/100</small></strong>
          <small>{health.healthLabel.toLowerCase()}</small>
        </div>
        <div className="hm-sub-summary-card">
          <span>✂️ Reviewable spend</span>
          <strong className="hm-sub-risk-value">{fmtShort$(health.estimatedYearlySavings)}</strong>
          <small>yearly</small>
        </div>
      </div>

      <MobileCollapseSection storageKey="hm_sub_health_open" title="💚 Subscription health" badge={`${health.healthScore}/100`}>
        <SubscriptionHealthPanel health={health} />
      </MobileCollapseSection>
      <MobileCollapseSection storageKey="hm_sub_alerts_open" title="🔔 Alerts" badge={`${renewingSoon.length + alerts.length}`}>
        <SubscriptionAlertsPanel renewingSoon={renewingSoon} health={health} alerts={alerts} subById={subById} />
      </MobileCollapseSection>
      <MobileCollapseSection storageKey="hm_sub_category_open" title="📊 Spend by category" badge={fmt$(totalMonthly)}>
        <SubscriptionCategoryPanel catTotals={catTotals} totalMonthly={totalMonthly} />
      </MobileCollapseSection>

      <div className="hm-sub-list-head">
        <span>{filterStatus === "all" ? "All subscriptions" : `${filterStatus} subscriptions`}</span>
        <div className="hm-sub-add-actions">
          <button type="button" className="hm-vault-manual-btn hm-sub-quickadd-btn"
            onClick={() => { setEditTarget(null); setView("quickadd"); }}>
            ⚡ Quick add
          </button>
          <button type="button" className="hm-veh-add-btn hm-sub-manualadd-btn"
            onClick={() => { setEditTarget({ ...BLANK_FORM }); setView("addform"); }}>
            + Manual add
          </button>
        </div>
      </div>

      <div className="hm-sub-filters">
        <div className="hm-vault-tabs">
          {["active","trial","paused","cancelled","all"].map(s => (
            <button key={s} type="button"
              className={`hm-vault-tab${filterStatus === s ? " is-active" : ""}`}
              onClick={() => setFilterStatus(s)}
              style={{ textTransform: "capitalize" }}>
              {s}
            </button>
          ))}
        </div>
        <select className="hm-auth-input hm-sub-cat-filter"
          value={filterCat} onChange={e => setFilterCat(e.target.value)}>
          <option value="all">All categories</option>
          {Object.entries(CATEGORY_META).map(([v, m]) => (
            <option key={v} value={v}>{m.emoji} {m.label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="hm-sub-loading">Loading subscriptions…</div>
      ) : filtered.length === 0 ? (
        <div className="hm-veh-empty-garage">
          <span>📋</span>
          <h3>No subscriptions found</h3>
          <p>{filterStatus !== "all" || filterCat !== "all"
            ? "Try clearing your filters."
            : "Add your first subscription to start tracking."}</p>
          <button type="button" className="hm-veh-add-btn"
            onClick={() => { setEditTarget({ ...BLANK_FORM }); setView("addform"); }}>
            + Add subscription
          </button>
        </div>
      ) : (
        <div className="hm-sub-list">
          {filtered.map(sub => (
            <SubCard
              key={sub.id}
              sub={sub}
              onSelect={() => {
                if (presentation === "sheet") openInsightSheet(sub);
                else { setSelected(sub); setView("detail"); }
              }}
              onEdit={() => { setEditTarget(sub); setView("editform"); }}
              onDelete={setConfirmDelete}
              onOpenActions={openActionSheet}
            />
          ))}
        </div>
      )}

      {actionItems.length > 0 && (
        <MobileCollapseSection storageKey="hm_sub_actions_open" title="✅ Recommended actions" badge={`${actionItems.length}`}>
          <div className="hm-sub-action-grid">
            {actionItems.map(item => (
              <SubscriptionActionCard
                key={item.key}
                item={item}
                onOpen={() => {
                  if (item.reportOnly) setView("report");
                  else if (item.sub) { setSelected(item.sub); setView("detail"); }
                  else setView("report");
                }}
              />
            ))}
          </div>
        </MobileCollapseSection>
      )}

      <MobileCollapseSection storageKey="hm_sub_ai_open" title="✦ AI waste report" badge={signals.length ? `${signals.length} signals` : "on demand"}>
        <SubscriptionIntelligencePanel
          health={health}
          subs={subs}
          signals={signals}
          onReport={() => setView("report")}
          onOpenSub={sub => { setSelected(sub); setView("detail"); }}
        />
      </MobileCollapseSection>

      <SubscriptionsAskBar />
    </div>
  );
}
